import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import CommonService from '../../../services/common.service';
import StorageService from '../../../services/StorageService';
import { tradeCompanyProfileFromResponse } from '../../website/logisticsRfqBuyerPrefill';
import { useProductDetailsMaster } from '../../../hooks/useProductDetailsMaster';
import { useFactoryDetailsMaster } from '../../../hooks/useFactoryDetailsMaster';
import '../../../assets/styles/shipping_manifest_create.scss';

function parseJsonObjectMaybe(value, fallback = null) {
  if (value == null || value === '') return fallback;
  if (typeof value === 'object' && !Array.isArray(value)) return value;
  if (typeof value === 'string') {
    try {
      const o = JSON.parse(value);
      return o && typeof o === 'object' && !Array.isArray(o) ? o : fallback;
    } catch {
      return fallback;
    }
  }
  return fallback;
}

/** `GET /eway-bill/:id` may embed buyer `company_profile` + `user_login` as `buyerCompanyProfile`. */
function pickEmbeddedBuyerCompanyFromEwayBill(ewayBill) {
  if (!ewayBill || typeof ewayBill !== 'object') return null;
  const p = ewayBill.buyerCompanyProfile ?? ewayBill.buyer_company_profile;
  if (!p || typeof p !== 'object' || Array.isArray(p)) return null;
  return p;
}

/** `GET .../rfq/submission/:id/buyer-company` — axios body is `{ success, data: { buyerCompany } }` (CommonService returns `res.data`). */
function unwrapBuyerCompanyFromBuyerCompanyApi(buyerRes) {
  if (!buyerRes || typeof buyerRes !== 'object') return null;
  if (buyerRes.success === false) return null;
  const inner = buyerRes.data !== undefined && buyerRes.data !== null ? buyerRes.data : buyerRes;
  const bc = inner?.buyerCompany ?? inner?.data?.buyerCompany ?? buyerRes.buyerCompany;
  if (bc != null && typeof bc === 'object' && !Array.isArray(bc)) return bc;
  if (inner != null && typeof inner === 'object' && !Array.isArray(inner) && (inner.company_id != null || inner.company_name != null)) {
    return inner;
  }
  if (buyerRes.company_id != null || buyerRes.company_name != null) return buyerRes;
  return null;
}

export default function ShippingManifestCreate() {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Get data from navigation state if available
  const ewayBillId = location.state?.ewayBillId;
  const ewayNumber = location.state?.ewayNumber;
  const invoiceId = location.state?.invoiceId;
  const invoiceNumber = location.state?.invoiceNumber;
  const manifestId = location.state?.manifestId; // For editing existing manifest
  const logisticsGenericRfqId = location.state?.logisticsGenericRfqId ?? null;
  const logisticsQuoteId = location.state?.logisticsQuoteId ?? null;
  /** RFQ-linked shipment (used for From-side my-profile when there is no commercial invoice in state). */
  const isLogisticsFlow = Boolean(logisticsQuoteId || logisticsGenericRfqId) && !invoiceId;
  
  const [loading, setLoading] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [fetchedInvoice, setFetchedInvoice] = useState(null);
  const [fetchedPO, setFetchedPO] = useState(null);
  const [sellerCompanyId, setSellerCompanyId] = useState(null);
  const [buyerCompanyId, setBuyerCompanyId] = useState(null);

  // Master Data Hooks
  const { attributeUnitOptions } = useProductDetailsMaster();
  const { shippingTermsOptions, modeOfTransportOptions, loading: factoryDetailsLoading } = useFactoryDetailsMaster();

  const [formData, setFormData] = useState({
    // From (Exporter/Shipper)
    fromCompanyName: '',
    fromContactPerson: '',
    fromGSTIN: '',
    fromIEC: '',
    fromAddressLine1: '',
    fromAddressLine2: '',
    fromCountry: 'India',
    fromState: '',
    fromCity: '',
    fromPostalCode: '',
    fromEmail: '',
    fromPhone: '',
    
    // To (Consignee/Importer)
    toCompanyName: '',
    toContactPerson: '',
    toTaxID: '',
    toEmail: '',
    toAddressLine1: '',
    toAddressLine2: '',
    toCountry: '',
    toState: '',
    toCity: '',
    toPostalCode: '',
    toPhone: '',
    
    // Order Reference Details
    poNumber: '',
    orderDate: '',
    currency: 'INR',
    subtotalAmount: '',
    buyerName: '',
    supplierName: '',
    commercialInvoiceNumber: '',
    
    // Shipment Details
    preferredDeliveryPort: '',
    deliveryPortReference: '',
    invoiceBillNumber: '',
    modeOfTransport: 'Road Freight',
    incoterms: '',
    estimatedDeliveryDate: '',
    ewayBillNumber: '',
    actualArrivalDate: '',
    billOfLading: '',
    airwayBill: '',
    carrierVessel: '',
    vesselFlightNumber: '',
    route: '',
    departureDate: '',
    expectedArrivalDate: '',
    finalDestination: '',
    
    // Vehicle & Driver Details
    vehicleType: 'Truck',
    vehicleNumber: '',
    vehicleCapacity: '',
    lrNumber: '',
    gpsTrackingID: '',
    gpsProvider: '',
    insurancePolicyNo: '',
    insuranceProvider: '',
    driverName: '',
    driverLicenseNo: '',
    driverContactNumber: '',
    
    // Goods Handling Requirements
    hazardousMaterial: false,
    hazardClass: '',
    unNumber: '',
    temperatureControlled: false,
    temperatureRange: '',
    fragile: false,
    fragileMinHeight: '',
    stackable: false,
    stackableMaxHeight: '',
    keepDry: false,
    keepUpright: false,
    specialInstructions: '',
    
    // Additional Notes
    additionalNotes: ''
  });

  const [items, setItems] = useState([]);

  const [documents, setDocuments] = useState({
    invoiceCopy: null,
    ewayBill: null,
    packingList: null,
    qualityCert: null,
    certOfOrigin: null,
    otherDocs: null
  });

  // Store uploaded document paths
  const [uploadedDocuments, setUploadedDocuments] = useState({
    invoiceCopy: null,
    ewayBill: null,
    packingList: null,
    qualityCert: null,
    certOfOrigin: null,
    otherDocs: null
  });

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // Generate LR Number (similar to PO and Invoice number generation)
  const generateLRNumber = () => {
    const now = new Date();
    const year = now.getFullYear();
    
    // Generate a unique 6-digit number from timestamp
    // Use the last 6 digits of the timestamp (milliseconds since epoch)
    // This ensures uniqueness while keeping the format readable
    const timestamp = now.getTime();
    const sequence = String(timestamp).slice(-6).padStart(6, '0');
    
    return `LR-${year}-${sequence}`;
  };

  // Format date for input field (YYYY-MM-DD)
  const formatDateForInput = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '';
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    } catch (error) {
      return '';
    }
  };

  // Format currency amount
  const formatCurrencyAmount = (amount) => {
    if (!amount && amount !== 0) return '';
    const numAmount = parseFloat(amount);
    if (isNaN(numAmount)) return '';
    return numAmount.toLocaleString('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  // Auto-generate LR Number on component mount (similar to PO and Invoice numbers)
  useEffect(() => {
    setFormData(prev => {
      if (!prev.lrNumber) {
        return {
          ...prev,
          lrNumber: generateLRNumber()
        };
      }
        return prev;
    });
  }, []); // Run once on mount

  // Fetch and populate data
  useEffect(() => {
    const fetchAndPopulateData = async () => {
      // If editing (manifestId exists), load existing manifest data
      if (manifestId && !dataLoaded) {
        try {
          setLoading(true);
          
          // Fetch existing manifest
          const manifestResponse = await CommonService.get(`/shipping-manifest/${manifestId}`);
          if (manifestResponse && manifestResponse.success && manifestResponse.data) {
            const manifest = manifestResponse.data;
            setLoadedManifestStatus(manifest.status || '');
            
            // Populate form data from existing manifest
            setFormData(prev => ({
              ...prev,
              manifestNumber: manifest.manifest_number || '',
              manifestDate: manifest.manifest_date ? formatDateForInput(manifest.manifest_date) : '',
              lrNumber: manifest.lr_number || '',
              fromCompanyName: manifest.from_company_name || '',
              fromContactPerson: manifest.from_contact_person || '',
              fromGSTIN: manifest.from_gstin || '',
              fromIEC: manifest.from_iec || '',
              fromCountry: manifest.from_country || '',
              fromState: manifest.from_state || '',
              fromCity: manifest.from_city || '',
              fromPostalCode: manifest.from_postal_code || '',
              fromEmail: manifest.from_email || '',
              fromPhone: manifest.from_phone || '',
              fromAddressLine1: manifest.from_address_line1 || '',
              fromAddressLine2: manifest.from_address_line2 || '',
              toCompanyName: manifest.to_company_name || '',
              toContactPerson: manifest.to_contact_person || '',
              toTaxID: manifest.to_tax_id || '',
              toEmail: manifest.to_email || '',
              toCountry: manifest.to_country || '',
              toState: manifest.to_state || '',
              toCity: manifest.to_city || '',
              toPostalCode: manifest.to_postal_code || '',
              toPhone: manifest.to_phone || '',
              toAddressLine1: manifest.to_address_line1 || '',
              toAddressLine2: manifest.to_address_line2 || '',
              poNumber: manifest.po_number || '',
              orderDate: manifest.order_date ? formatDateForInput(manifest.order_date) : '',
              currency: manifest.currency || 'INR',
              subtotalAmount: manifest.subtotal_amount || '',
              buyerName: manifest.buyer_name || '',
              supplierName: manifest.supplier_name || '',
              commercialInvoiceNumber: manifest.commercial_invoice_number || '',
              preferredDeliveryPort: manifest.preferred_delivery_port || '',
              deliveryPortReference: manifest.delivery_port_reference || '',
              invoiceBillNumber: manifest.invoice_bill_number || '',
              modeOfTransport: manifest.mode_of_transport || '',
              incoterms: manifest.incoterms || '',
              estimatedDeliveryDate: manifest.estimated_delivery_date ? formatDateForInput(manifest.estimated_delivery_date) : '',
              actualArrivalDate: manifest.actual_arrival_date ? formatDateForInput(manifest.actual_arrival_date) : '',
              billOfLading: manifest.bill_of_lading || '',
              airwayBill: manifest.airway_bill || '',
              carrierVessel: manifest.carrier_vessel || '',
              vesselFlightNumber: manifest.vessel_flight_number || '',
              route: manifest.route || '',
              departureDate: manifest.departure_date ? formatDateForInput(manifest.departure_date) : '',
              expectedArrivalDate: manifest.expected_arrival_date ? formatDateForInput(manifest.expected_arrival_date) : '',
              finalDestination: manifest.final_destination || '',
              vehicleType: manifest.vehicle_type || '',
              vehicleNumber: manifest.vehicle_number || '',
              vehicleCapacity: manifest.vehicle_capacity || '',
              gpsTrackingId: manifest.gps_tracking_id || '',
              gpsProvider: manifest.gps_provider || '',
              insurancePolicyNo: manifest.insurance_policy_no || '',
              insuranceProvider: manifest.insurance_provider || '',
              driverName: manifest.driver_name || '',
              driverLicenseNo: manifest.driver_license_no || '',
              driverContactNumber: manifest.driver_contact_number || '',
              totalPackages: manifest.total_packages || '',
              totalWeightKg: manifest.total_weight_kg || '',
              additionalNotes: manifest.additional_notes || '',
              goodsHandlingRequirements: manifest.goods_handling_requirements 
                ? (typeof manifest.goods_handling_requirements === 'string' 
                    ? JSON.parse(manifest.goods_handling_requirements) 
                    : manifest.goods_handling_requirements)
                : {}
            }));
            
            // Populate items
            if (manifest.items && manifest.items.length > 0) {
              setItems(manifest.items.map(item => ({
                itemSequence: item.item_sequence || 0,
                hsnProductCode: item.hsn_product_code || '',
                productDescription: item.product_description || '',
                qtyShipped: item.qty_shipped || 0,
                qtyTotal: item.qty_total || 0,
                shippingQty: item.shipping_qty || 0,
                uom: item.uom || '',
                packageType: item.package_type || '',
                noPackages: item.no_packages || 0,
                weightPerPkgKg: item.weight_per_pkg_kg || 0,
                totalWeightKg: item.total_weight_kg || 0
              })));
            }
            
            // Store invoice and PO IDs if available
            if (manifest.invoice_id) {
              setFetchedInvoice({ id: manifest.invoice_id });
              
              // Fetch E-way Bill Number from invoice if not already set
              if (!ewayNumber && !manifest.eway_bill_number) {
                try {
                  const ewayBillsResponse = await CommonService.get(`/eway-bill/invoice/${manifest.invoice_id}`);
                  if (ewayBillsResponse && ewayBillsResponse.success && ewayBillsResponse.data && ewayBillsResponse.data.length > 0) {
                    // Get the latest E-way Bill (first one in the array, as they're ordered by created_datetime DESC)
                    const latestEWayBill = ewayBillsResponse.data[0];
                    if (latestEWayBill.ewb_number) {
                      setFormData(prev => ({
        ...prev,
                        ewayBillNumber: latestEWayBill.ewb_number
                      }));
                    }
                  }
                } catch (ewayError) {
                  console.warn('Error fetching E-way Bill for manifest:', ewayError);
                  // Continue without E-way Bill Number
                }
              }
            }
            if (manifest.purchase_order_id) {
              setFetchedPO({ id: manifest.purchase_order_id });
            }
            
            // Set E-way Bill Number from location state if available (takes priority)
            if (ewayNumber) {
              setFormData(prev => ({
                ...prev,
                ewayBillNumber: ewayNumber
              }));
            }
            
            // Load existing documents
            if (manifest.documents && manifest.documents.length > 0) {
              const docMap = {};
              manifest.documents.forEach(doc => {
                // Map document types back to our document keys
                const typeMap = {
                  'invoice': 'invoiceCopy',
                  'eway_bill': 'ewayBill',
                  'packing_list': 'packingList',
                  'quality_certificate': 'qualityCert',
                  'certificate_of_origin': 'certOfOrigin',
                  'other_document': 'otherDocs'
                };
                const docKey = typeMap[doc.document_type] || 'otherDocs';
                docMap[docKey] = doc.document_path;
              });
              setUploadedDocuments(docMap);
            }

            // If navigated from tracking "View Manifest", auto-open details modal.
            if (location.state?.openViewManifest) {
              setShowViewManifestModal(true);
            }
            
            setDataLoaded(true);
            setLoading(false);
            return;
          }
        } catch (error) {
          console.error('Error fetching existing manifest:', error);
          toast.error('Failed to load existing manifest data');
        } finally {
          setLoading(false);
        }
      }
      
      if (dataLoaded || !ewayBillId) {
        // If no required IDs, ensure LR number and E-way Bill Number are set and return
        if (!dataLoaded) {
          setFormData(prev => ({
            ...prev,
            lrNumber: prev.lrNumber || generateLRNumber(),
            ewayBillNumber: prev.ewayBillNumber || ewayNumber || ''
          }));
          setDataLoaded(true);
        }
        return;
      }

      try {
        setLoading(true);

        // Fetch E-Way Bill (required for both invoice & logistics flows)
        const ewayBillResponse = await CommonService.get(`/eway-bill/${ewayBillId}`);
        const ewayBill = ewayBillResponse?.success ? ewayBillResponse.data : null;

        // Fetch Invoice (invoice/PO flow only)
        let invoice = null;
        if (invoiceId) {
          const invoiceResponse = await CommonService.get(`/seller-invoice/${invoiceId}`);
          invoice = invoiceResponse?.success ? invoiceResponse.data : null;
        }
        setFetchedInvoice(invoice);
        
        // Store company IDs from invoice
        if (invoice) {
          if (invoice.seller_company_id) {
            setSellerCompanyId(invoice.seller_company_id);
          }
          if (invoice.buyer_company_id) {
            setBuyerCompanyId(invoice.buyer_company_id);
          }
        }

        // Fetch PO if available
        let poData = null;
        if (invoice?.purchase_order_id) {
          try {
            const poResponse = await CommonService.get(`/purchase-order/${invoice.purchase_order_id}`);
            poData = poResponse?.success ? poResponse.data : null;
            setFetchedPO(poData);
            
            // Store company IDs from PO (these are more reliable than invoice)
            if (poData) {
              if (poData.seller_company_id) {
                setSellerCompanyId(poData.seller_company_id);
              }
              if (poData.buyer_company_id) {
                setBuyerCompanyId(poData.buyer_company_id);
              }
            }
          } catch (poError) {
            console.warn('Error fetching PO:', poError);
          }
        }

        // Fetch Seller Company Profile
        // - Invoice/PO flow: use invoice seller_company_id
        // - Logistics flow: use current logged-in user's company profile (user_login-backed email/phone/contact details)
        let sellerCompany = null;
        if (isLogisticsFlow) {
          try {
            const myProfileRes = await CommonService.get('/company/my-profile');
            sellerCompany = myProfileRes?.success ? myProfileRes.data : null;
          } catch (sellerError) {
            console.warn('Error fetching my company profile:', sellerError);
          }
        } else {
          const sellerCompanyIdFromInvoice = invoice?.seller_company_id;
          const sellerCompanyIdFromEWay = ewayBill?.seller_company_id;
          const sellerCompanyIdToFetch = sellerCompanyIdFromInvoice || sellerCompanyIdFromEWay || null;
          if (sellerCompanyIdToFetch) {
            try {
              const sellerCompanyResponse = await CommonService.get(`/company/profile/${sellerCompanyIdToFetch}`);
              sellerCompany = sellerCompanyResponse?.success ? sellerCompanyResponse.data : null;
            } catch (sellerError) {
              console.warn('Error fetching seller company:', sellerError);
            }
          }
        }

        // Fetch Buyer Company Profile (`company_profile` + `user_login` for contact name / email / phone)
        // - Prefer embedded `buyerCompanyProfile` on GET /eway-bill/:id (server join; works for logistics providers)
        // - Else invoice buyer_company_id → GET /company/profile/:id
        // - Else RFQ buyer-company API when submission id is known
        let buyerCompany = pickEmbeddedBuyerCompanyFromEwayBill(ewayBill);
        const buyerCompanyIdFromInvoice = invoice?.buyer_company_id ?? invoice?.buyerCompanyId ?? null;
        const buyerCompanyIdFromEWay = ewayBill?.buyer_company_id ?? ewayBill?.buyerCompanyId ?? null;
        let buyerCompanyIdToFetch = buyerCompanyIdFromInvoice || buyerCompanyIdFromEWay || null;

        const rfqSubmissionIdRaw =
          logisticsGenericRfqId ??
          ewayBill?.logistics_generic_rfq_id ??
          ewayBill?.logisticsGenericRfqId ??
          null;
        const rfqSubmissionId =
          rfqSubmissionIdRaw != null && String(rfqSubmissionIdRaw).trim() !== ''
            ? Number(rfqSubmissionIdRaw)
            : NaN;

        // Always load RFQ buyer profile when we have a submission id — even if `invoiceId` is set in navigation
        // (otherwise To* falls back only to e-way text fields and wrong `to_name` like "Seller" hides real consignee).
        if (Number.isFinite(rfqSubmissionId) && rfqSubmissionId > 0) {
          try {
            const buyerRes = await CommonService.get(
              `/company/logistics-providers/rfq/submission/${rfqSubmissionId}/buyer-company`
            );
            buyerCompany = unwrapBuyerCompanyFromBuyerCompanyApi(buyerRes);
          } catch (e) {
            console.warn('Error fetching buyer company for logistics submission:', e);
          }
        }
        if (!buyerCompany && buyerCompanyIdToFetch) {
          try {
            const buyerCompanyResponse = await CommonService.get(`/company/profile/${buyerCompanyIdToFetch}`);
            buyerCompany = buyerCompanyResponse?.success ? buyerCompanyResponse.data : null;
          } catch (buyerError) {
            console.warn('Error fetching buyer company:', buyerError);
          }
        }
        if (!buyerCompany && ewayBill?.buyer_id != null) {
          const su = StorageService.getUser ? StorageService.getUser() : null;
          const sessionUid = Number(su?.user_login_id ?? su?.id);
          const ewayBuyerUid = Number(ewayBill.buyer_id);
          if (
            Number.isFinite(sessionUid) &&
            sessionUid > 0 &&
            Number.isFinite(ewayBuyerUid) &&
            ewayBuyerUid > 0 &&
            sessionUid === ewayBuyerUid
          ) {
            try {
              const myRes = await CommonService.get('/company/my-profile');
              buyerCompany = tradeCompanyProfileFromResponse(myRes);
            } catch (e) {
              console.warn('Error fetching my-profile for consignee (buyer) fields:', e);
            }
          }
        }

        // Populate form data
        setFormData(prev => {
          const newFormData = { ...prev };

        // From (Seller/Exporter) - Priority: Company Profile > Invoice > E-Way Bill
        if (sellerCompany) {
          newFormData.fromCompanyName = sellerCompany.company_name || '';
          newFormData.fromGSTIN = sellerCompany.tax_number || '';
          newFormData.fromIEC = sellerCompany.iec_number || '';
          if (sellerCompany.address) {
            newFormData.fromAddressLine1 = sellerCompany.address.address || '';
            newFormData.fromAddressLine2 = sellerCompany.address.landmark || '';
            newFormData.fromCountry = sellerCompany.address.country || 'India';
            newFormData.fromState = sellerCompany.address.state || '';
            newFormData.fromCity = sellerCompany.address.city || '';
            newFormData.fromPostalCode = sellerCompany.address.pincode || '';
          }
          // Contact Person Name from contact_details (alternate_contact_name) or contact_name
          if (sellerCompany.contact_details) {
            newFormData.fromContactPerson = sellerCompany.contact_details.alternate_contact_name || 
              sellerCompany.contact_details.contact_name || '';
          }
          // Email: Priority: user_email (from user_login) > contact_details.alternate_email/contact_email > alt_email
          newFormData.fromEmail = sellerCompany.user_email || 
            (sellerCompany.contact_details && (sellerCompany.contact_details.alternate_email || sellerCompany.contact_details.contact_email)) || 
            sellerCompany.alt_email || '';
          // Phone: Priority: user_phone (from user_login) > contact_details.alternate_contact_number/contact_number > alt_contact
          newFormData.fromPhone = sellerCompany.user_phone || 
            (sellerCompany.contact_details && (sellerCompany.contact_details.alternate_contact_number || sellerCompany.contact_details.contact_number)) || 
            sellerCompany.alt_contact || '';
        } else if (invoice) {
          newFormData.fromCompanyName = invoice.seller_company_name || '';
          newFormData.fromGSTIN = invoice.seller_gstin || '';
          if (Array.isArray(invoice.seller_address)) {
            newFormData.fromAddressLine1 = invoice.seller_address.join(', ') || '';
          } else {
            newFormData.fromAddressLine1 = invoice.seller_address || '';
          }
          newFormData.fromState = invoice.shipping_state || '';
          newFormData.fromCity = invoice.shipping_city || '';
          newFormData.fromPostalCode = invoice.shipping_pincode || '';
        }
        if (ewayBill) {
          newFormData.fromCompanyName = newFormData.fromCompanyName || ewayBill.from_name || '';
          newFormData.fromGSTIN = newFormData.fromGSTIN || ewayBill.from_gstin || '';
          newFormData.fromAddressLine1 = newFormData.fromAddressLine1 || ewayBill.from_address || '';
          newFormData.fromCountry = newFormData.fromCountry || ewayBill.from_country || 'India';
          newFormData.fromState = newFormData.fromState || ewayBill.from_state || '';
          newFormData.fromCity = newFormData.fromCity || ewayBill.from_city || '';
          newFormData.fromPostalCode = newFormData.fromPostalCode || ewayBill.from_pincode || '';
        }

        // To (Buyer/Consignee) - Priority: Company Profile (incl. user_login) > Invoice > E-Way Bill
        if (buyerCompany) {
          const addr = parseJsonObjectMaybe(buyerCompany.address, {});
          const bcd = parseJsonObjectMaybe(buyerCompany.contact_details, null);
          newFormData.toCompanyName =
            (buyerCompany.company_name || buyerCompany.companyName || '').trim() || '';
          newFormData.toTaxID = buyerCompany.tax_number || 'N/A (International)';
          if (addr && Object.keys(addr).length > 0) {
            newFormData.toAddressLine1 = addr.address || '';
            newFormData.toAddressLine2 = addr.landmark || '';
            newFormData.toCountry = addr.country || '';
            newFormData.toState = addr.state || '';
            newFormData.toCity = addr.city || '';
            newFormData.toPostalCode = addr.pincode || '';
          }
          // Contact Person Name: user_login.name (API: user_name) > contact_details
          newFormData.toContactPerson =
            buyerCompany.user_name ||
            buyerCompany.userName ||
            (bcd && (bcd.alternate_contact_name || bcd.contact_name || bcd.contactName)) ||
            '';
          // Email: user_login.email > contact_details > alt_email
          newFormData.toEmail =
            buyerCompany.user_email ||
            buyerCompany.userEmail ||
            (bcd && (bcd.alternate_email || bcd.contact_email || bcd.contactEmail)) ||
            buyerCompany.alt_email ||
            '';
          // Phone: user_login.phone > contact_details > alt_contact
          newFormData.toPhone =
            buyerCompany.user_phone ||
            buyerCompany.userPhone ||
            (bcd && (bcd.alternate_contact_number || bcd.contact_number || bcd.contactNumber)) ||
            buyerCompany.alt_contact ||
            '';
        } else if (invoice) {
          newFormData.toCompanyName = invoice.buyer_company_name || '';
          newFormData.toTaxID = invoice.buyer_tax_id || 'N/A (International)';
          if (Array.isArray(invoice.buyer_address)) {
            newFormData.toAddressLine1 = invoice.buyer_address.join(', ') || '';
          } else {
            newFormData.toAddressLine1 = invoice.buyer_address || '';
          }
          newFormData.toState = invoice.shipping_state || '';
          newFormData.toCity = invoice.shipping_city || '';
          newFormData.toPostalCode = invoice.shipping_pincode || '';
        }
        if (ewayBill) {
          newFormData.toCompanyName = newFormData.toCompanyName || ewayBill.to_name || '';
          newFormData.toTaxID = newFormData.toTaxID || ewayBill.to_gstin || 'N/A (International)';
          newFormData.toAddressLine1 = newFormData.toAddressLine1 || ewayBill.to_address || '';
          newFormData.toCountry = newFormData.toCountry || ewayBill.to_country || '';
          newFormData.toState = newFormData.toState || ewayBill.to_state || '';
          newFormData.toCity = newFormData.toCity || ewayBill.to_city || '';
          newFormData.toPostalCode = newFormData.toPostalCode || ewayBill.to_pincode || '';
        }

        // Order Reference Details
        if (poData) {
          newFormData.poNumber = poData.po_number || `PO-${poData.id}` || '';
          newFormData.orderDate = formatDateForInput(poData.po_date) || '';
          newFormData.currency = poData.currency || invoice?.currency || 'INR';
          newFormData.buyerName = newFormData.toCompanyName || '';
          newFormData.supplierName = newFormData.fromCompanyName || '';
          
          // Map incoterms from PO if available (check both freight_terms and incoterms fields, also check po_data structure)
          const poIncoterms = poData.freight_terms || poData.incoterms || poData.freightTerms || 
            (poData.po_data && (poData.po_data.freightTerms || poData.po_data.incoterms)) || '';
          if (poIncoterms && shippingTermsOptions && shippingTermsOptions.length > 0) {
            const foundIncoterm = shippingTermsOptions.find(opt => 
              opt.label === poIncoterms || 
              opt.label?.toLowerCase() === poIncoterms.toLowerCase() ||
              String(opt.value) === String(poIncoterms)
            );
            newFormData.incoterms = foundIncoterm ? foundIncoterm.value : poIncoterms;
          } else if (poIncoterms) {
            newFormData.incoterms = poIncoterms;
          }
        } else if (invoice) {
          newFormData.poNumber = invoice.po_number || '';
          newFormData.orderDate = formatDateForInput(invoice.invoice_date) || '';
          newFormData.currency = invoice.currency || 'INR';
          newFormData.buyerName = newFormData.toCompanyName || '';
          newFormData.supplierName = newFormData.fromCompanyName || '';
        }

        // Invoice details
        if (invoice) {
          newFormData.commercialInvoiceNumber = invoice.invoice_number || invoiceNumber || '';
          newFormData.invoiceBillNumber = invoice.invoice_number || invoiceNumber || '';
          
          // Calculate subtotal from line items
          if (invoice.line_items && Array.isArray(invoice.line_items) && invoice.line_items.length > 0) {
            const subtotal = invoice.line_items.reduce((sum, item) => {
              const itemTotal = parseFloat(item.total) || 0;
              return sum + itemTotal;
            }, 0);
            newFormData.subtotalAmount = formatCurrencyAmount(subtotal || invoice.grand_total || 0);
          } else {
            newFormData.subtotalAmount = formatCurrencyAmount(invoice.grand_total || 0);
          }
        }

        // Shipment Details - Priority: E-Way Bill > PO > Invoice
        // Set E-way Bill Number from ewayBill or location state
        if (ewayBill) {
          newFormData.ewayBillNumber = ewayBill.ewb_number || ewayNumber || '';
        } else if (ewayNumber) {
          // Fallback: use ewayNumber from location state if ewayBill fetch failed
          newFormData.ewayBillNumber = ewayNumber;
        }
        
        if (ewayBill) {
          
          // Map mode of transport
          const transportModeValue = ewayBill.transport_mode || '';
          if (transportModeValue && modeOfTransportOptions && modeOfTransportOptions.length > 0) {
            const foundTransport = modeOfTransportOptions.find(opt => 
              opt.label === transportModeValue || 
              opt.label?.toLowerCase() === transportModeValue.toLowerCase() ||
              String(opt.value) === String(transportModeValue)
            );
            newFormData.modeOfTransport = foundTransport ? foundTransport.value : transportModeValue;
          } else {
            newFormData.modeOfTransport = transportModeValue || '';
          }
        }
        
        // Shipment Details from PO
        if (poData) {
          // Preferred Delivery Port from PO (port_of_discharge)
          if (!newFormData.preferredDeliveryPort) {
            if (poData.port_of_discharge) {
              newFormData.preferredDeliveryPort = poData.port_of_discharge;
            } else if (poData.po_data && poData.po_data.portOfDischarge) {
              newFormData.preferredDeliveryPort = poData.po_data.portOfDischarge;
            }
          }
          
          // Estimated Delivery Date from PO
          if (!newFormData.estimatedDeliveryDate) {
            if (poData.expected_delivery_date) {
              newFormData.estimatedDeliveryDate = formatDateForInput(poData.expected_delivery_date);
            } else if (poData.po_data && poData.po_data.expectedDeliveryDate) {
              newFormData.estimatedDeliveryDate = formatDateForInput(poData.po_data.expectedDeliveryDate);
            }
          }
        }
        
        // Vehicle & Driver Details from E-Way Bill
        if (ewayBill) {
          // Vehicle Type
          if (ewayBill.vehicle_type) {
            // Map vehicle type to dropdown options if needed
            const vehicleTypeValue = ewayBill.vehicle_type;
            const vehicleTypeOptions = ['Truck', 'Container', 'Van', 'Trailer', 'Other'];
            const foundVehicleType = vehicleTypeOptions.find(opt => 
              opt.toLowerCase() === vehicleTypeValue.toLowerCase() ||
              vehicleTypeValue.toLowerCase().includes(opt.toLowerCase())
            );
            newFormData.vehicleType = foundVehicleType || vehicleTypeValue || newFormData.vehicleType || 'Truck';
          }
          
          // Vehicle Number
          if (ewayBill.vehicle_number) {
            newFormData.vehicleNumber = ewayBill.vehicle_number;
          }
          
          // Vehicle Type Description (could be used for capacity if available)
          if (ewayBill.vehicle_type_description && !newFormData.vehicleCapacity) {
            // Try to extract capacity from description if it contains weight/tonnage info
            const capacityMatch = ewayBill.vehicle_type_description.match(
              /(\d+(?:\.\d+)?)\s*(ton|tons|tonne|tonnes|kg)/i
            );
            if (capacityMatch) {
              // store numeric-only; unit is implicit in UI/business docs
              newFormData.vehicleCapacity = String(capacityMatch[1]);
            }
          }
        }

        // Generate LR Number if not set (always auto-generated, not populated from e-way bill)
        if (!newFormData.lrNumber) {
          newFormData.lrNumber = generateLRNumber();
        }

          return newFormData;
        });

        // Populate items from invoice line items
        if (invoice && invoice.line_items && Array.isArray(invoice.line_items) && invoice.line_items.length > 0) {
          // Get PO line items if available for HSN code fallback
          const poLineItems = (poData && poData.line_items && Array.isArray(poData.line_items)) 
            ? poData.line_items 
            : (poData && poData.po_data && poData.po_data.lineItems && Array.isArray(poData.po_data.lineItems))
            ? poData.po_data.lineItems
            : [];

          const populatedItems = invoice.line_items.map((item, index) => {
            // Extract HSN code - Priority: hsn_sac (from invoice) > hsnSac > hsn_code > hsnCode > PO line item > extract from description
            let hsnCode = item.hsn_sac || item.hsnSac || item.hsn_code || item.hsnCode || '';
            
            // If HSN not found in invoice line item, try to get from PO line item using po_line_item_id
            if (!hsnCode && item.po_line_item_id && poLineItems.length > 0) {
              const poLineItem = poLineItems.find(poItem => 
                (poItem.line_item_id || poItem.id) === item.po_line_item_id
              );
              if (poLineItem) {
                hsnCode = poLineItem.hsn_code || poLineItem.hsnCode || poLineItem.hsn || '';
              }
            }
            
            // If still not found, try to extract from description
            if (!hsnCode && item.description) {
              const extractedHsn = item.description.match(/\b\d{4,8}\b/)?.[0];
              if (extractedHsn) {
                hsnCode = extractedHsn;
              }
            }
            
            // Clean product description (remove HSN codes)
            let productDesc = item.description || item.productName || item.product_name || item.name || '';
            if (productDesc) {
              productDesc = productDesc
                .replace(/\bHSN\s*[:\-]?\s*\d{4,8}\b/gi, '')
                .replace(/\b\d{4,8}\s*\(HSN\)/gi, '')
                .replace(/\bHSN\s*Code\s*[:\-]?\s*\d{4,8}\b/gi, '')
                .trim();
            }

            const quantity = parseFloat(item.quantity) || 0;
            const unitValue = item.unit || item.uom || item.unit_of_measurement || '';
            
            // Map UOM value to ID if master data is available
            let mappedUom = unitValue;
            if (unitValue && attributeUnitOptions && attributeUnitOptions.length > 0) {
              // Try to find by value (ID)
              const foundByValue = attributeUnitOptions.find(opt => 
                String(opt.value) === String(unitValue)
              );
              if (foundByValue) {
                mappedUom = foundByValue.value;
              } else {
                // Try to find by label
                const foundByLabel = attributeUnitOptions.find(opt => 
                  opt.label === String(unitValue) || 
                  opt.label?.toLowerCase() === String(unitValue).toLowerCase()
                );
                if (foundByLabel) {
                  mappedUom = foundByLabel.value;
                } else {
                  // Keep original value if not found
                  mappedUom = unitValue;
                }
              }
            }

            return {
              id: index + 1,
              hsnProductCode: hsnCode || '',
              productDescription: productDesc,
              qtyShipped: quantity.toString(),
              qtyTotal: quantity.toString(),
              shippingQty: quantity.toString(),
              uom: mappedUom || '',
              packageType: '',
              noPackages: '',
              weightPerPkg: '',
              totalWeight: '0'
            };
          });
          setItems(populatedItems);
        }

        setDataLoaded(true);
        toast.success('Data loaded successfully');
      } catch (error) {
        console.error('Error fetching data:', error);
        toast.error('Failed to load data. Please fill manually.');
        // Still generate LR number even if data fetch fails
        setFormData(prev => ({
          ...prev,
          lrNumber: prev.lrNumber || generateLRNumber(),
          ewayBillNumber: prev.ewayBillNumber || ewayNumber || ''
        }));
        setDataLoaded(true);
      } finally {
        setLoading(false);
      }
    };

    fetchAndPopulateData();
  }, [ewayBillId, invoiceId, ewayNumber, invoiceNumber, dataLoaded]);

  const handleItemChange = (id, field, value) => {
    setItems(prev => prev.map(item => {
      if (item.id === id) {
        const updated = { ...item, [field]: value };
        // Recalculate total weight
        if (field === 'noPackages' || field === 'weightPerPkg') {
          const packages = parseFloat(updated.noPackages) || 0;
          const weightPerPkg = parseFloat(updated.weightPerPkg) || 0;
          updated.totalWeight = (packages * weightPerPkg).toString();
        }
        return updated;
      }
      return item;
    }));
  };

  const handleAddItem = () => {
    const newItem = {
      id: items.length + 1,
      hsnProductCode: '',
      productDescription: '',
      qtyShipped: '',
      qtyTotal: '',
      shippingQty: '',
      uom: '',
      packageType: '',
      noPackages: '',
      weightPerPkg: '',
      totalWeight: '0'
    };
    setItems([...items, newItem]);
  };

  const handleDeleteItem = (id) => {
    setItems(items.filter(item => item.id !== id));
  };

  const handleFileUpload = (docType, file) => {
    if (!file) return;
    
    setDocuments(prev => ({
      ...prev,
      [docType]: file
    }));
    
    // Reset uploaded path when new file is selected
    setUploadedDocuments(prev => ({
      ...prev,
      [docType]: null
    }));
  };

  // Upload a single file
  const uploadFile = async (file, docType) => {
    try {
      const uploadFormData = new FormData();
      uploadFormData.append('file', file);
      
      // Get company name from form data - must not be empty
      const companyName = formData.fromCompanyName?.trim() || 'Default_Company';
      if (!companyName || companyName === '') {
        throw new Error('Company name is required for file upload');
      }
      
      // Use 'company' category (valid category) for shipping manifest documents
      // The backend validator only allows: 'company', 'factory', 'product', 'quality', 'clarification', 'quotation'
      const category = 'company';
      
      // Map document types to file types
      const fileTypeMap = {
        invoiceCopy: 'invoice',
        ewayBill: 'eway_bill',
        packingList: 'packing_list',
        qualityCert: 'quality_certificate',
        certOfOrigin: 'certificate_of_origin',
        otherDocs: 'other_document'
      };
      
      const fileType = fileTypeMap[docType] || docType;
      
      uploadFormData.append('category', category);
      uploadFormData.append('companyName', companyName);
      uploadFormData.append('fileType', fileType);
      
      const response = await CommonService.post('/upload/single', uploadFormData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      if (response && response.success && response.data && response.data.filePath) {
        return response.data.filePath;
      } else {
        throw new Error(response?.message || 'File upload failed');
      }
    } catch (error) {
      console.error(`Error uploading ${docType}:`, error);
      throw error;
    }
  };

  // Upload all documents
  const uploadAllDocuments = async () => {
    const documentTypes = ['invoiceCopy', 'ewayBill', 'packingList', 'qualityCert', 'certOfOrigin', 'otherDocs'];
    const uploadedPaths = {};
    
    for (const docType of documentTypes) {
      if (documents[docType]) {
        try {
          const filePath = await uploadFile(documents[docType], docType);
          uploadedPaths[docType] = filePath;
        } catch (error) {
          console.error(`Failed to upload ${docType}:`, error);
          toast.error(`Failed to upload ${docType}. Please try again.`);
          throw error; // Stop if any upload fails
        }
      }
    }
    
    return uploadedPaths;
  };

  const calculateTotals = () => {
    const totalPackages = items.reduce((sum, item) => sum + (parseFloat(item.noPackages) || 0), 0);
    const totalWeight = items.reduce((sum, item) => sum + (parseFloat(item.totalWeight) || 0), 0);
    return { totalPackages, totalWeight };
  };

  const { totalPackages, totalWeight } = calculateTotals();

  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [showViewManifestModal, setShowViewManifestModal] = useState(false);
  const [loadedManifestStatus, setLoadedManifestStatus] = useState('');

  const packageTypeOptions = ['Carton', 'Wooden Crate', 'Pallet', 'Box', 'Bag', 'Other'];
  const vehicleTypeOptions = ['Truck', 'Container', 'Van', 'Trailer', 'Other'];
  
  // Use master data for UOM and Incoterms
  const uomOptions = attributeUnitOptions || [];
  const incotermsOptions = shippingTermsOptions || [];

  const handlePreview = () => {
    setShowPreviewModal(true);
  };
  
  // Validate mandatory fields
  const validateMandatoryFields = (isSubmit = false) => {
    const errors = [];
    
    // Required fields for both draft and submit
    if (!formData.fromCompanyName) {
      errors.push('From Company Name is required');
    }
    if (!formData.toCompanyName) {
      errors.push('To Company Name is required');
    }
    if (!isLogisticsFlow && !formData.poNumber) {
      errors.push('PO Number is required');
    }
    
    // Required fields for submit (stricter validation)
    if (isSubmit) {
      if (!formData.preferredDeliveryPort || formData.preferredDeliveryPort.trim() === '') {
        errors.push('Preferred Delivery Port is required');
      }
      if (!formData.modeOfTransport || formData.modeOfTransport.trim() === '') {
        errors.push('Mode of Transport is required');
      }
      if (!formData.incoterms || formData.incoterms.trim() === '') {
        errors.push('Incoterms is required');
      }
      if (!formData.estimatedDeliveryDate || formData.estimatedDeliveryDate.trim() === '') {
        errors.push('Estimated Delivery Date is required');
      }
      if (!formData.vehicleType || formData.vehicleType.trim() === '') {
        errors.push('Vehicle Type is required');
      }
      if (!items || items.length === 0) {
        errors.push('At least one item is required');
      }
    }
    
    return errors;
  };

  // Prepare manifest data for API
  const prepareManifestData = async (status) => {
    // Calculate totals
    const totalPackages = items.reduce((sum, item) => sum + (parseFloat(item.noPackages) || 0), 0);
    const totalWeight = items.reduce((sum, item) => sum + (parseFloat(item.totalWeight) || 0), 0);

    // Prepare goods handling requirements
    const goodsHandlingRequirements = {
      hazardousMaterial: formData.hazardousMaterial || false,
      hazardClass: formData.hazardClass || null,
      unNumber: formData.unNumber || null,
      temperatureControlled: formData.temperatureControlled || false,
      minTemperature: formData.minTemperature || null,
      maxTemperature: formData.maxTemperature || null,
      fragile: formData.fragile || false,
      specialHandling: formData.specialHandling || null
    };

    // Get purchase order ID and invoice ID from location state or fetched data
    const invoiceIdValue = invoiceId || location.state?.invoiceId || null;
    const poId = location.state?.poId || fetchedInvoice?.purchase_order_id || fetchedPO?.id || null;

    // Validate required fields
    if (!isLogisticsFlow && !poId) {
      throw new Error('Purchase Order ID is required. Please ensure you are creating manifest from a valid order.');
    }

    // Upload documents and prepare document data
    const documentList = [];
    const documentTypeMap = {
      invoiceCopy: { type: 'invoice', name: 'Invoice Copy', required: 1 },
      ewayBill: { type: 'eway_bill', name: 'E-way Bill', required: 0 },
      packingList: { type: 'packing_list', name: 'Packing List', required: 0 },
      qualityCert: { type: 'quality_certificate', name: 'Quality Certificate', required: 0 },
      certOfOrigin: { type: 'certificate_of_origin', name: 'Certificate of Origin', required: 0 },
      otherDocs: { type: 'other_document', name: 'Other Document', required: 0 }
    };

    // Upload all documents
    for (const [docKey, docInfo] of Object.entries(documentTypeMap)) {
      // If a new file is selected, upload it
      if (documents[docKey]) {
        try {
          // Check if already uploaded (from previous save or existing manifest)
          let filePath = uploadedDocuments[docKey];
          if (!filePath) {
            // Upload new file
            filePath = await uploadFile(documents[docKey], docKey);
            // Store uploaded path
            setUploadedDocuments(prev => ({
              ...prev,
              [docKey]: filePath
            }));
          }
          
          if (filePath) {
            documentList.push({
              document_type: docInfo.type,
              document_name: docInfo.name,
              document_path: filePath,
              is_required: docInfo.required
            });
          }
        } catch (error) {
          console.error(`Error uploading ${docKey}:`, error);
          toast.error(`Failed to upload ${docInfo.name}. Please try again.`);
          // Continue with other documents even if one fails
        }
      } else if (uploadedDocuments[docKey]) {
        // If no new file but existing document path exists (from editing), preserve it
        documentList.push({
          document_type: docInfo.type,
          document_name: docInfo.name,
          document_path: uploadedDocuments[docKey],
          is_required: docInfo.required
        });
      }
    }

    return {
      purchase_order_id: isLogisticsFlow ? null : poId,
      invoice_id: invoiceIdValue,
      logistics_generic_rfq_id: isLogisticsFlow ? (logisticsGenericRfqId || null) : null,
      logistics_quote_id: isLogisticsFlow ? (logisticsQuoteId || null) : null,
      manifest_date: new Date().toISOString().split('T')[0],
      status: status,
      from_company_name: formData.fromCompanyName || '',
      from_contact_person: formData.fromContactPerson || null,
      from_gstin: formData.fromGSTIN || null,
      from_iec: formData.fromIEC || null,
      from_country: formData.fromCountry || 'India',
      from_state: formData.fromState || null,
      from_city: formData.fromCity || null,
      from_postal_code: formData.fromPostalCode || null,
      from_email: formData.fromEmail || null,
      from_phone: formData.fromPhone || null,
      from_address_line1: formData.fromAddressLine1 || null,
      from_address_line2: formData.fromAddressLine2 || null,
      to_company_name: formData.toCompanyName || '',
      to_contact_person: formData.toContactPerson || null,
      to_tax_id: formData.toTaxID || null,
      to_email: formData.toEmail || null,
      to_country: formData.toCountry || '',
      to_state: formData.toState || null,
      to_city: formData.toCity || null,
      to_postal_code: formData.toPostalCode || null,
      to_phone: formData.toPhone || null,
      to_address_line1: formData.toAddressLine1 || null,
      to_address_line2: formData.toAddressLine2 || null,
      // DB column may be NOT NULL; logistics flow uses empty string (UI section hidden).
      po_number: isLogisticsFlow ? '' : (formData.poNumber || ''),
      order_date: formData.orderDate || null,
      currency: formData.currency || 'INR',
      subtotal_amount: formData.subtotalAmount ? parseFloat(formData.subtotalAmount.replace(/[^\d.-]/g, '')) : null,
      buyer_name: formData.buyerName || null,
      supplier_name: formData.supplierName || null,
      commercial_invoice_number: formData.commercialInvoiceNumber || null,
      preferred_delivery_port: formData.preferredDeliveryPort || null,
      delivery_port_reference: formData.deliveryPortReference || null,
      invoice_bill_number: formData.invoiceBillNumber || null,
      mode_of_transport: formData.modeOfTransport || 'Road Freight',
      incoterms: formData.incoterms || null,
      estimated_delivery_date: formData.estimatedDeliveryDate || null,
      actual_arrival_date: formData.actualArrivalDate || null,
      bill_of_lading: formData.billOfLading || null,
      airway_bill: formData.airwayBill || null,
      carrier_vessel: formData.carrierVessel || null,
      vessel_flight_number: formData.vesselFlightNumber || null,
      route: formData.route || null,
      departure_date: formData.departureDate || null,
      expected_arrival_date: formData.expectedArrivalDate || null,
      final_destination: formData.finalDestination || null,
      vehicle_type: formData.vehicleType || null,
      vehicle_number: formData.vehicleNumber || null,
      vehicle_capacity: formData.vehicleCapacity || null,
      lr_number: formData.lrNumber || null,
      gps_tracking_id: formData.gpsTrackingID || null,
      gps_provider: formData.gpsProvider || null,
      insurance_policy_no: formData.insurancePolicyNo || null,
      insurance_provider: formData.insuranceProvider || null,
      driver_name: formData.driverName || null,
      driver_license_no: formData.driverLicenseNo || null,
      driver_contact_number: formData.driverContactNumber || null,
      goods_handling_requirements: goodsHandlingRequirements,
      total_packages: totalPackages || 0,
      total_weight_kg: totalWeight || 0,
      additional_notes: formData.additionalNotes || null,
      seller_id: null, // Will be set by backend from user context
      buyer_id: null, // Will be set by backend from user context
      seller_company_id: sellerCompanyId,
      buyer_company_id: buyerCompanyId,
      items: items.map((item, index) => ({
        item_sequence: index + 1,
        hsn_product_code: item.hsnProductCode || null,
        product_description: item.productDescription || '',
        qty_shipped: parseFloat(item.qtyShipped) || 0,
        qty_total: item.qtyTotal ? parseFloat(item.qtyTotal) : null,
        shipping_qty: item.shippingQty ? parseFloat(item.shippingQty) : null,
        uom: item.uom || '',
        package_type: item.packageType || null,
        no_packages: item.noPackages ? parseFloat(item.noPackages) : null,
        weight_per_pkg_kg: item.weightPerPkg ? parseFloat(item.weightPerPkg) : null,
        total_weight_kg: item.totalWeight ? parseFloat(item.totalWeight) : null
      })),
      documents: documentList
    };
  };

  // Handle Save as Draft
  const handleSaveDraft = async () => {
    try {
      setLoading(true);
      
      // Validate mandatory fields for draft
      const validationErrors = validateMandatoryFields(false);
      if (validationErrors.length > 0) {
        validationErrors.forEach(error => toast.error(error));
        setLoading(false);
        return;
      }
      
      const manifestData = await prepareManifestData('Draft');
      
      // If manifestId exists, update instead of create
      if (manifestId) {
        const response = await CommonService.put(`/shipping-manifest/${manifestId}`, manifestData);
        
        if (response && response.success) {
          toast.success('Shipping manifest updated successfully');
          setTimeout(() => {
            navigate('/shipping-manifest-generation-list');
          }, 1000);
        } else {
          toast.error(response?.error || 'Failed to update shipping manifest');
        }
      } else {
        const response = await CommonService.post('/shipping-manifest', manifestData);
        
        if (response && response.success) {
          toast.success('Shipping manifest saved as draft successfully');
          setTimeout(() => {
            navigate('/shipping-manifest-generation-list');
          }, 1000);
        } else {
          toast.error(response?.error || 'Failed to save shipping manifest draft');
        }
      }
    } catch (error) {
      console.error('Error saving shipping manifest draft:', error);
      toast.error(error.response?.data?.error || error.message || 'Failed to save shipping manifest draft');
    } finally {
      setLoading(false);
    }
  };

  // Handle Submit Manifest
  const handleSubmit = async () => {
    try {
      setLoading(true);
      
      // Validate mandatory fields for submit (stricter)
      const validationErrors = validateMandatoryFields(true);
      if (validationErrors.length > 0) {
        validationErrors.forEach(error => toast.error(error));
        setLoading(false);
        return;
      }
      
      const manifestData = await prepareManifestData('Submitted');
      
      // If manifestId exists, update instead of create
      if (manifestId) {
        const response = await CommonService.put(`/shipping-manifest/${manifestId}`, manifestData);
        
        if (response && response.success) {
          toast.success('Shipping manifest updated and submitted successfully');
          setTimeout(() => {
            navigate(isLogisticsFlow ? '/dashboard/logistics/rfqs' : '/shipping-manifest-generation-list');
          }, 1000);
        } else {
          toast.error(response?.error || 'Failed to update shipping manifest');
        }
      } else {
        const response = await CommonService.post('/shipping-manifest', manifestData);
        
        if (response && response.success) {
          toast.success('Shipping manifest submitted successfully');
          setTimeout(() => {
            navigate(isLogisticsFlow ? '/dashboard/logistics/rfqs' : '/shipping-manifest-generation-list');
          }, 1000);
        } else {
          toast.error(response?.error || 'Failed to submit shipping manifest');
        }
      }
    } catch (error) {
      console.error('Error submitting shipping manifest:', error);
      toast.error(error.response?.data?.error || error.message || 'Failed to submit shipping manifest');
    } finally {
      setLoading(false);
    }
  };
  
  // Helper function to get UOM label
  const getUomLabel = (unitValue) => {
    if (!unitValue) return '';
    if (!attributeUnitOptions || attributeUnitOptions.length === 0) return unitValue;
    
    const unitValueNum = isNaN(unitValue) ? null : Number(unitValue);
    const foundByValue = attributeUnitOptions.find(opt => {
      if (String(opt.value) === String(unitValue)) return true;
      if (unitValueNum !== null && !isNaN(opt.value)) {
        return Number(opt.value) === unitValueNum;
      }
      return false;
    });
    
    if (foundByValue) {
      return foundByValue.label || unitValue;
    }
    
    const foundByLabel = attributeUnitOptions.find(opt => 
      opt.label === String(unitValue) || 
      opt.label?.toLowerCase() === String(unitValue).toLowerCase()
    );
    if (foundByLabel) {
      return foundByLabel.label;
    }
    
    return unitValue;
  };

  return (
    <div className="shipping-manifest-create">
      <div className="sm-main-content">
        {/* Header */}
        <div className="sm-header">
          <div className="sm-header-left">
            <nav aria-label="breadcrumb" className="sm-breadcrumb">
              <ol className="sm-breadcrumb-list">
                <li className="sm-breadcrumb-item">
                  <a href="/" className="sm-breadcrumb-link">Home</a>
                </li>
                <li className="sm-breadcrumb-item sm-breadcrumb-active" aria-current="page">
                  Shipping Manifest
                </li>
              </ol>
            </nav>
            <h1 className="sm-main-title">Create Shipping Manifest</h1>
            <p className="sm-subtitle">
              {loading ? 'Loading data...' : (formData.poNumber ? `Auto-populated from PO: ${formData.poNumber} | LR No: ${formData.lrNumber}` : 'Fill in the details below')}
            </p>
          </div>
          <div className="sm-header-right">
            <div className="sm-header-actions">
              <button className="sm-back-btn" onClick={() => navigate(-1)}>
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="m12 19-7-7 7-7"></path>
                  <path d="M19 12H5"></path>
                </svg>
                Back
              </button>
              <button className="sm-btn sm-btn-primary sm-btn-preview" onClick={handlePreview}>
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                  <circle cx="12" cy="12" r="3"></circle>
                </svg>
                Preview & Submit Manifest
              </button>
              <button 
                className="sm-btn sm-btn-draft" 
                onClick={handleSaveDraft}
                disabled={loading}
              >
                {loading ? 'Saving...' : 'Save as Draft'}
              </button>
            </div>
          </div>
        </div>

        {/* Main Card */}
        <div className="sm-main-card">
          {/* From (Exporter/Shipper) Section */}
          <div className="sm-section-wrapper">
            <div className="sm-section-header-with-bar">
              <h3 className="sm-section-title sm-section-title-orange">From (Exporter/Shipper)</h3>
            </div>
            <div className="sm-section sm-section-orange">
              <div className="sm-section-content">
              {/* First Row - 6 columns */}
              <div className="sm-form-grid sm-form-grid-6col">
                <div className="sm-form-field">
                  <label>Company / Shipper Name</label>
                  <input
                    type="text"
                    className="sm-input sm-input-readonly"
                    name="fromCompanyName"
                    value={formData.fromCompanyName}
                    readOnly
                  />
                </div>
                <div className="sm-form-field">
                  <label>Contact Person Name</label>
                  <input
                    type="text"
                    className="sm-input sm-input-readonly"
                    name="fromContactPerson"
                    value={formData.fromContactPerson}
                    readOnly
                  />
                </div>
                <div className="sm-form-field">
                  <label>GSTIN</label>
                  <input
                    type="text"
                    className="sm-input sm-input-readonly"
                    name="fromGSTIN"
                    value={formData.fromGSTIN}
                    readOnly
                  />
                </div>
                <div className="sm-form-field">
                  <label>IEC / Export Code</label>
                  <input
                    type="text"
                    className="sm-input sm-input-readonly"
                    name="fromIEC"
                    value={formData.fromIEC}
                    readOnly
                  />
                </div>
                <div className="sm-form-field">
                  <label>Email</label>
                  <input
                    type="email"
                    className="sm-input sm-input-readonly"
                    name="fromEmail"
                    value={formData.fromEmail}
                    readOnly
                  />
                </div>
                <div className="sm-form-field">
                  <label>Phone</label>
                  <input
                    type="text"
                    className="sm-input sm-input-readonly"
                    name="fromPhone"
                    value={formData.fromPhone}
                    readOnly
                  />
                </div>
              </div>
              
              {/* Second Row - Address (3 columns: 2 for Address Line 1, 1 for Address Line 2) */}
              <div className="sm-form-grid sm-form-grid-3col">
                <div className="sm-form-field sm-form-field-span-2">
                  <label>Address Line 1</label>
                  <input
                    type="text"
                    className="sm-input sm-input-readonly"
                    name="fromAddressLine1"
                    value={formData.fromAddressLine1}
                    readOnly
                  />
                </div>
                <div className="sm-form-field">
                  <label>Address Line 2</label>
                  <input
                    type="text"
                    className="sm-input sm-input-readonly"
                    name="fromAddressLine2"
                    value={formData.fromAddressLine2}
                    placeholder="Optional"
                    readOnly
                  />
                </div>
              </div>
              
              {/* Third Row - Location (4 columns) */}
              <div className="sm-form-grid sm-form-grid-4col">
                <div className="sm-form-field">
                  <label>Country</label>
                  <input
                    type="text"
                    className="sm-input sm-input-readonly"
                    name="fromCountry"
                    value={formData.fromCountry}
                    readOnly
                  />
                </div>
                <div className="sm-form-field">
                  <label>State / Province</label>
                  <input
                    type="text"
                    className="sm-input sm-input-readonly"
                    name="fromState"
                    value={formData.fromState}
                    readOnly
                  />
                </div>
                <div className="sm-form-field">
                  <label>City</label>
                  <input
                    type="text"
                    className="sm-input sm-input-readonly"
                    name="fromCity"
                    value={formData.fromCity}
                    readOnly
                  />
                </div>
                <div className="sm-form-field">
                  <label>Postal Code / Pincode</label>
                  <input
                    type="text"
                    className="sm-input sm-input-readonly"
                    name="fromPostalCode"
                    value={formData.fromPostalCode}
                    readOnly
                  />
                </div>
              </div>
              </div>
            </div>
          </div>


          {/* To (Consignee/Importer) Section */}
          <div className="sm-section-wrapper">
            <div className="sm-section-header-with-bar">
              <h3 className="sm-section-title sm-section-title-green">To (Consignee/Importer)</h3>
            </div>
            <div className="sm-section sm-section-green">
              <div className="sm-section-content">
                {/* First Row - 5 columns */}
                <div className="sm-form-grid sm-form-grid-5col">
                  <div className="sm-form-field">
                    <label>Company / Consignee Name</label>
                    <input
                      type="text"
                      className="sm-input sm-input-readonly"
                      name="toCompanyName"
                      value={formData.toCompanyName}
                      readOnly
                    />
                  </div>
                  <div className="sm-form-field">
                    <label>Contact Person Name</label>
                    <input
                      type="text"
                      className="sm-input sm-input-readonly"
                      name="toContactPerson"
                      value={formData.toContactPerson}
                      readOnly
                    />
                  </div>
                  <div className="sm-form-field">
                    <label>Tax ID / Import Code</label>
                    <input
                      type="text"
                      className="sm-input sm-input-readonly"
                      name="toTaxID"
                      value={formData.toTaxID}
                      readOnly
                    />
                  </div>
                  <div className="sm-form-field">
                    <label>Email</label>
                    <input
                      type="email"
                      className="sm-input sm-input-readonly"
                      name="toEmail"
                      value={formData.toEmail}
                      readOnly
                    />
                  </div>
                  <div className="sm-form-field">
                    <label>Phone</label>
                    <input
                      type="text"
                      className="sm-input sm-input-readonly"
                      name="toPhone"
                      value={formData.toPhone}
                      readOnly
                    />
                  </div>
                </div>
                
                {/* Second Row - Address (3 columns: 2 for Address Line 1, 1 for Address Line 2) */}
                <div className="sm-form-grid sm-form-grid-3col">
                  <div className="sm-form-field sm-form-field-span-2">
                    <label>Address Line 1</label>
                    <input
                      type="text"
                      className="sm-input sm-input-readonly"
                      name="toAddressLine1"
                      value={formData.toAddressLine1}
                      readOnly
                    />
                  </div>
                  <div className="sm-form-field">
                    <label>Address Line 2</label>
                    <input
                      type="text"
                      className="sm-input sm-input-readonly"
                      name="toAddressLine2"
                      value={formData.toAddressLine2}
                      placeholder="Optional"
                      readOnly
                    />
                  </div>
                </div>
                
                {/* Third Row - Location (4 columns) */}
                <div className="sm-form-grid sm-form-grid-4col">
                  <div className="sm-form-field">
                    <label>Country</label>
                    <input
                      type="text"
                      className="sm-input sm-input-readonly"
                      name="toCountry"
                      value={formData.toCountry}
                      readOnly
                    />
                  </div>
                  <div className="sm-form-field">
                    <label>State / Province</label>
                    <input
                      type="text"
                      className="sm-input sm-input-readonly"
                      name="toState"
                      value={formData.toState}
                      readOnly
                    />
                  </div>
                  <div className="sm-form-field">
                    <label>City</label>
                    <input
                      type="text"
                      className="sm-input sm-input-readonly"
                      name="toCity"
                      value={formData.toCity}
                      readOnly
                    />
                  </div>
                  <div className="sm-form-field">
                    <label>Postal Code / Pincode</label>
                    <input
                      type="text"
                      className="sm-input sm-input-readonly"
                      name="toPostalCode"
                      value={formData.toPostalCode}
                      readOnly
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          

          {/* Order Reference Details Section (hide for logistics flow) */}
          {!isLogisticsFlow ? (
            <div className="sm-section-wrapper">
              <div className="sm-section-header-with-bar">
                <h3 className="sm-section-title sm-section-title-blue">Order Reference Details</h3>
              </div>
              <div className="sm-section sm-section-blue">
                <div className="sm-section-content">
                  {/* First Row - 5 columns */}
                  <div className="sm-form-grid sm-form-grid-5col">
                    <div className="sm-form-field">
                      <label>PO Number</label>
                      <input
                        type="text"
                        className="sm-input sm-input-readonly"
                        name="poNumber"
                        value={formData.poNumber}
                        readOnly
                      />
                    </div>
                    <div className="sm-form-field">
                      <label>Order Date</label>
                      <input
                        type="date"
                        className="sm-input sm-input-date sm-input-readonly"
                        name="orderDate"
                        value={formData.orderDate}
                        readOnly
                      />
                    </div>
                    <div className="sm-form-field">
                      <label>Currency</label>
                      <input
                        type="text"
                        className="sm-input sm-input-readonly"
                        name="currency"
                        value={formData.currency}
                        readOnly
                      />
                    </div>
                    <div className="sm-form-field">
                      <label>Subtotal Amount</label>
                      <input
                        type="text"
                        className="sm-input sm-input-readonly"
                        name="subtotalAmount"
                        value={`${formData.currency} ${formData.subtotalAmount}`}
                        readOnly
                      />
                    </div>
                    <div className="sm-form-field">
                      <label>Commercial Invoice Number</label>
                      <input
                        type="text"
                        className="sm-input sm-input-readonly"
                        name="commercialInvoiceNumber"
                        value={formData.commercialInvoiceNumber}
                        readOnly
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : null}

      

          {/* Shipment Details Section */}
          <div className="sm-section-wrapper">
            <div className="sm-section-header-with-bar">
              <h3 className="sm-section-title sm-section-title-purple">Shipment Details</h3>
            </div>
            <div className="sm-section sm-section-purple">
              <div className="sm-section-content">
                {/* Single Row - 6 columns */}
                <div className="sm-form-grid sm-form-grid-4col">
                  <div className="sm-form-field">
                    <label>Preferred Delivery Port<span className="sm-required">*</span></label>
                    <select
                      className="sm-input sm-input-select"
                      name="preferredDeliveryPort"
                      value={formData.preferredDeliveryPort}
                      onChange={handleInputChange}
                      required
                    >
                      <option value="">Select Preferred Delivery Port</option>
                      <option value="Port of Hamburg, Germany">Port of Hamburg, Germany</option>
                      <option value="Port of Rotterdam, Netherlands">Port of Rotterdam, Netherlands</option>
                      <option value="Port of Antwerp, Belgium">Port of Antwerp, Belgium</option>
                    </select>
                  </div>
                  <div className="sm-form-field">
                    <label>Delivery Port Reference</label>
                    <input
                      type="text"
                      className="sm-input"
                      name="deliveryPortReference"
                      value={formData.deliveryPortReference}
                      onChange={handleInputChange}
                    />
                  </div>
                  {!isLogisticsFlow ? (
                    <div className="sm-form-field">
                      <label>Invoice/Bill Number</label>
                      <input
                        type="text"
                        className="sm-input sm-input-readonly"
                        name="invoiceBillNumber"
                        value={formData.invoiceBillNumber}
                        readOnly
                      />
                    </div>
                  ) : null}
                <div className="sm-form-field">
                  <label>E-way Bill Number</label>
                  <input
                    type="text"
                    className="sm-input sm-input-readonly"
                    name="ewayBillNumber"
                    value={formData.ewayBillNumber}
                    readOnly
                  />
                </div>
                  <div className="sm-form-field">
                    <label>Mode of Transport<span className="sm-required">*</span></label>
                    <select
                      className="sm-input sm-input-select"
                      name="modeOfTransport"
                      value={formData.modeOfTransport}
                      onChange={handleInputChange}
                    >
                      <option value="">Select Mode of Transport</option>
                      {modeOfTransportOptions && modeOfTransportOptions.length > 0 ? (
                        modeOfTransportOptions.map(option => (
                          <option key={option.value} value={option.value}>{option.label}</option>
                        ))
                      ) : (
                        // Fallback options if master data is not loaded
                        <>
                          <option value="Sea Freight">Sea Freight</option>
                          <option value="Air Freight">Air Freight</option>
                          <option value="Road Freight">Road Freight</option>
                          <option value="Rail Freight">Rail Freight</option>
                        </>
                      )}
                    </select>
                  </div>
                  <div className="sm-form-field">
                    <label>Incoterms<span className="sm-required">*</span></label>
                    <select
                      className="sm-input sm-input-select"
                      name="incoterms"
                      value={formData.incoterms}
                      onChange={handleInputChange}
                    >
                      <option value="">Select Incoterms</option>
                      {incotermsOptions && incotermsOptions.length > 0 ? (
                        incotermsOptions.map(option => (
                          <option key={option.value} value={option.value}>{option.label}</option>
                        ))
                      ) : (
                        // Fallback options if master data is not loaded
                        <>
                          <option value="FOB - Free on Board">FOB - Free on Board</option>
                          <option value="CIF - Cost, Insurance and Freight">CIF - Cost, Insurance and Freight</option>
                          <option value="EXW - Ex Works">EXW - Ex Works</option>
                          <option value="DDP - Delivered Duty Paid">DDP - Delivered Duty Paid</option>
                        </>
                      )}
                    </select>
                  </div>
                  <div className="sm-form-field">
                    <label>Estimated Delivery Date<span className="sm-required">*</span></label>
                    <input
                      type="date"
                      className="sm-input sm-input-date"
                      name="estimatedDeliveryDate"
                      value={formData.estimatedDeliveryDate}
                      onChange={handleInputChange}
                      placeholder="dd-mm-yyyy"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

        

          {/* Vehicle & Driver Details Section */}
          <div className="sm-section-wrapper">
            <div className="sm-section-header-with-bar">
              <h3 className="sm-section-title sm-section-title-indigo">Vehicle & Driver Details</h3>
            </div>
            <div className="sm-section sm-section-vehicle">
              <div className="sm-section-content">
                {/* First Row - 4 columns */}
                <div className="sm-form-grid sm-form-grid-4col">
                  <div className="sm-form-field">
                    <label>Vehicle Type<span className="sm-required">*</span></label>
                    <select
                      className="sm-input sm-input-select"
                      name="vehicleType"
                      value={formData.vehicleType}
                      onChange={handleInputChange}
                    >
                      {vehicleTypeOptions.map(option => (
                        <option key={option} value={option}>{option}</option>
                      ))}
                    </select>
                  </div>
                  <div className="sm-form-field">
                    <label>Vehicle Number</label>
                    <input
                      type="text"
                      className="sm-input"
                      name="vehicleNumber"
                      value={formData.vehicleNumber}
                      onChange={handleInputChange}
                      placeholder="e.g., KA-01-AB-1234"
                    />
                  </div>
                  <div className="sm-form-field">
                    <label>Vehicle Capacity</label>
                    <input
                      type="number"
                      className="sm-input"
                      name="vehicleCapacity"
                      value={formData.vehicleCapacity}
                      onChange={handleInputChange}
                      min="0"
                      step="0.01"
                      placeholder="e.g., 10"
                    />
                  </div>
                  <div className="sm-form-field">
                    <label>LR Number (Auto-generated)</label>
                    <input
                      type="text"
                      className="sm-input sm-input-readonly"
                      name="lrNumber"
                      value={formData.lrNumber}
                      readOnly
                    />
                  </div>
                </div>
                
                {/* Second Row - 4 columns */}
                <div className="sm-form-grid sm-form-grid-4col">
                  <div className="sm-form-field">
                    <label>GPS Tracking ID</label>
                    <input
                      type="text"
                      className="sm-input"
                      name="gpsTrackingID"
                      value={formData.gpsTrackingID}
                      onChange={handleInputChange}
                      placeholder="e.g., GPS-TRK-123456789"
                    />
                  </div>
                  <div className="sm-form-field">
                    <label>GPS Provider / System</label>
                    <input
                      type="text"
                      className="sm-input"
                      name="gpsProvider"
                      value={formData.gpsProvider}
                      onChange={handleInputChange}
                      placeholder="e.g., Trimble, Geotab"
                    />
                  </div>
                  <div className="sm-form-field">
                    <label>Insurance Policy No.</label>
                    <input
                      type="text"
                      className="sm-input"
                      name="insurancePolicyNo"
                      value={formData.insurancePolicyNo}
                      onChange={handleInputChange}
                      placeholder="Policy number"
                    />
                  </div>
                  <div className="sm-form-field">
                    <label>Insurance Provider</label>
                    <input
                      type="text"
                      className="sm-input"
                      name="insuranceProvider"
                      value={formData.insuranceProvider}
                      onChange={handleInputChange}
                      placeholder="e.g., XYZ Insurance"
                    />
                  </div>
                </div>
                
                {/* Third Row - 3 columns */}
                <div className="sm-form-grid sm-form-grid-4col">
                  <div className="sm-form-field">
                    <label>Driver Name</label>
                    <input
                      type="text"
                      className="sm-input"
                      name="driverName"
                      value={formData.driverName}
                      onChange={handleInputChange}
                      placeholder="Driver full name"
                    />
                  </div>
                  <div className="sm-form-field">
                    <label>Driver License No.</label>
                    <input
                      type="text"
                      className="sm-input"
                      name="driverLicenseNo"
                      value={formData.driverLicenseNo}
                      onChange={handleInputChange}
                      placeholder="License number"
                    />
                  </div>
                  <div className="sm-form-field">
                    <label>Driver Contact Number</label>
                    <input
                      type="text"
                      className="sm-input"
                      name="driverContactNumber"
                      value={formData.driverContactNumber}
                      onChange={handleInputChange}
                      placeholder="Phone number"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

         

          {/* Items & Packages Details Section */}
          <div className="sm-section-wrapper">
            <div className="sm-section-header-with-bar">
              <h3 className="sm-section-title sm-section-title-orange">Items & Packages Details (Editable)</h3>
              <button className="sm-btn sm-btn-add-item" onClick={handleAddItem}>+ Add New Item</button>
            </div>
            <div className="sm-section sm-section-items">
            <div className="sm-table-wrapper">
              <table className="sm-items-table">
                <thead>
                  <tr>
                    <th>HSN/Product Code</th>
                    <th>Product Description</th>
                    <th>Qty Shipped</th>
                    <th>Qty Total</th>
                    <th>Shipping Qty</th>
                    <th>UOM</th>
                    <th>Package Type</th>
                    <th>No. Packages</th>
                    <th>Weight/Pkg (kg)</th>
                    <th>Total Weight</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map(item => (
                    <tr key={item.id}>
                      <td>
                        <input
                          type="text"
                          className="sm-table-input"
                          value={item.hsnProductCode}
                          onChange={(e) => handleItemChange(item.id, 'hsnProductCode', e.target.value)}
                        />
                      </td>
                      <td>
                        <input
                          type="text"
                          className="sm-table-input"
                          value={item.productDescription}
                          onChange={(e) => handleItemChange(item.id, 'productDescription', e.target.value)}
                        />
                      </td>
                      <td>
                        <input
                          type="text"
                          className="sm-table-input"
                          value={item.qtyShipped}
                          onChange={(e) => handleItemChange(item.id, 'qtyShipped', e.target.value)}
                        />
                      </td>
                      <td>
                        <input
                          type="text"
                          className="sm-table-input"
                          value={item.qtyTotal}
                          onChange={(e) => handleItemChange(item.id, 'qtyTotal', e.target.value)}
                        />
                      </td>
                      <td>
                        <input
                          type="text"
                          className="sm-table-input"
                          value={item.shippingQty}
                          onChange={(e) => handleItemChange(item.id, 'shippingQty', e.target.value)}
                        />
                      </td>
                      <td>
                        <select
                          className="sm-table-input sm-table-select"
                          value={item.uom}
                          onChange={(e) => handleItemChange(item.id, 'uom', e.target.value)}
                        >
                          <option value="">Select UOM</option>
                          {uomOptions.map(option => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td>
                        <select
                          className="sm-table-input sm-table-select"
                          value={item.packageType}
                          onChange={(e) => handleItemChange(item.id, 'packageType', e.target.value)}
                        >
                          <option value="">Select</option>
                          {packageTypeOptions.map(option => (
                            <option key={option} value={option}>{option}</option>
                          ))}
                        </select>
                      </td>
                      <td>
                        <input
                          type="text"
                          className="sm-table-input"
                          value={item.noPackages}
                          onChange={(e) => handleItemChange(item.id, 'noPackages', e.target.value)}
                        />
                      </td>
                      <td>
                        <input
                          type="text"
                          className="sm-table-input"
                          value={item.weightPerPkg}
                          onChange={(e) => handleItemChange(item.id, 'weightPerPkg', e.target.value)}
                        />
                      </td>
                      <td>
                        <input
                          type="text"
                          className="sm-table-input sm-table-input-readonly"
                          value={`${item.totalWeight} kg`}
                          readOnly
                        />
                      </td>
                      <td>
                        <button
                          className="sm-btn-delete"
                          onClick={() => handleDeleteItem(item.id)}
                          type="button"
                          title="Remove item"
                          aria-label="Delete item"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="sm-delete-icon">
                            <path d="M10 11v6"></path>
                            <path d="M14 11v6"></path>
                            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6"></path>
                            <path d="M3 6h18"></path>
                            <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                          </svg>
                        </button>
                      </td>
                    </tr>
                  ))}
                  <tr className="sm-table-totals-row">
                  <td colSpan="1"></td>
                  <td className="sm-totals-label">Totals:</td>
                  <td colSpan="5"></td>
                  <td className="sm-total-value">{totalPackages}</td>
                  <td colSpan="1"></td>
                  <td className="sm-total-value">{totalWeight} kg</td>
                  <td colSpan="1"></td>
                  </tr>
                </tbody>
              </table>
            </div>
            
            </div>
          </div>

         

          {/* Goods Handling Requirements and Supporting Documents - Two Column Layout */}
          <div className="sm-two-column-wrapper">
          {/* Goods Handling Requirements Section */}
          <div className="sm-section-wrapper">
            <div className="sm-section-header-with-bar">
              <h3 className="sm-section-title sm-section-title-red">Goods Handling Requirements</h3>
            </div>
            <div className="sm-section sm-section-handling">
              <div className="sm-handling-content">
                {/* Hazardous Material */}
                <div className="sm-handling-item-card">
                  <div className="sm-checkbox-field-row">
                    <label className="sm-checkbox-label">
                      <span className="sm-checkbox-wrapper">
                        <input
                          type="checkbox"
                          name="hazardousMaterial"
                          checked={formData.hazardousMaterial}
                          onChange={handleInputChange}
                          className="sm-checkbox-input"
                        />
                        {formData.hazardousMaterial && (
                          <span className="sm-checkbox-indicator">
                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M20 6 9 17l-5-5"></path>
                            </svg>
                          </span>
                        )}
                      </span>
                      <span className="sm-checkbox-text">Hazardous Material</span>
                    </label>
                  </div>
                  {formData.hazardousMaterial && (
                    <div className="sm-handling-fields-grid">
                      <input
                        type="text"
                        className="sm-input-conditional"
                        name="hazardClass"
                        value={formData.hazardClass}
                        onChange={handleInputChange}
                        placeholder="Hazard Class"
                      />
                      <input
                        type="text"
                        className="sm-input-conditional"
                        name="unNumber"
                        value={formData.unNumber}
                        onChange={handleInputChange}
                        placeholder="UN Number"
                      />
                    </div>
                  )}
                </div>

                {/* Temperature Controlled */}
                <div className="sm-handling-item-card">
                  <div className="sm-checkbox-field-row">
                    <label className="sm-checkbox-label">
                      <span className="sm-checkbox-wrapper">
                        <input
                          type="checkbox"
                          name="temperatureControlled"
                          checked={formData.temperatureControlled}
                          onChange={handleInputChange}
                          className="sm-checkbox-input"
                        />
                        {formData.temperatureControlled && (
                          <span className="sm-checkbox-indicator">
                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M20 6 9 17l-5-5"></path>
                            </svg>
                          </span>
                        )}
                      </span>
                      <span className="sm-checkbox-text">Temperature Controlled</span>
                    </label>
                  </div>
                  {formData.temperatureControlled && (
                    <div className="sm-handling-fields-single">
                      <input
                        type="text"
                        className="sm-input-conditional"
                        name="temperatureRange"
                        value={formData.temperatureRange}
                        onChange={handleInputChange}
                        placeholder="e.g., 2°C - 8°C"
                      />
                    </div>
                  )}
                </div>

                {/* Fragile & Stackable - Side by Side */}
                <div className="sm-handling-item-card">
                  <div className="sm-checkbox-grid-row">
                    <div className="sm-checkbox-field-row">
                      <label className="sm-checkbox-label">
                        <span className="sm-checkbox-wrapper">
                          <input
                            type="checkbox"
                            name="fragile"
                            checked={formData.fragile}
                            onChange={handleInputChange}
                            className="sm-checkbox-input"
                          />
                          {formData.fragile && (
                            <span className="sm-checkbox-indicator">
                              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M20 6 9 17l-5-5"></path>
                              </svg>
                            </span>
                          )}
                        </span>
                        <span className="sm-checkbox-text">Fragile</span>
                      </label>
                    </div>
                    <div className="sm-checkbox-field-row">
                      <label className="sm-checkbox-label">
                        <span className="sm-checkbox-wrapper">
                          <input
                            type="checkbox"
                            name="stackable"
                            checked={formData.stackable}
                            onChange={handleInputChange}
                            className="sm-checkbox-input"
                          />
                          {formData.stackable && (
                            <span className="sm-checkbox-indicator">
                              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M20 6 9 17l-5-5"></path>
                              </svg>
                            </span>
                          )}
                        </span>
                        <span className="sm-checkbox-text">Stackable</span>
                      </label>
                    </div>
                  </div>
                  {formData.stackable && (
                    <div className="sm-handling-fields-grid">
                      <input
                        type="text"
                        className="sm-input-conditional"
                        name="fragileMinHeight"
                        value={formData.fragileMinHeight}
                        onChange={handleInputChange}
                        placeholder="Min Height"
                      />
                      <input
                        type="text"
                        className="sm-input-conditional"
                        name="stackableMaxHeight"
                        value={formData.stackableMaxHeight}
                        onChange={handleInputChange}
                        placeholder="Max Height"
                      />
                    </div>
                  )}
                </div>

                {/* Keep Dry & Keep Upright - Side by Side */}
                <div className="sm-handling-item-card">
                  <div className="sm-checkbox-grid-row">
                    <div className="sm-checkbox-field-row">
                      <label className="sm-checkbox-label">
                        <span className="sm-checkbox-wrapper">
                          <input
                            type="checkbox"
                            name="keepDry"
                            checked={formData.keepDry}
                            onChange={handleInputChange}
                            className="sm-checkbox-input"
                          />
                          {formData.keepDry && (
                            <span className="sm-checkbox-indicator">
                              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M20 6 9 17l-5-5"></path>
                              </svg>
                            </span>
                          )}
                        </span>
                        <span className="sm-checkbox-text">Keep Dry</span>
                      </label>
                    </div>
                    <div className="sm-checkbox-field-row">
                      <label className="sm-checkbox-label">
                        <span className="sm-checkbox-wrapper">
                          <input
                            type="checkbox"
                            name="keepUpright"
                            checked={formData.keepUpright}
                            onChange={handleInputChange}
                            className="sm-checkbox-input"
                          />
                          {formData.keepUpright && (
                            <span className="sm-checkbox-indicator">
                              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M20 6 9 17l-5-5"></path>
                              </svg>
                            </span>
                          )}
                        </span>
                        <span className="sm-checkbox-text">Keep Upright</span>
                      </label>
                    </div>
                  </div>
                </div>

                {/* Special Instructions */}
                <div className="sm-handling-item-card">
                  <label className="sm-special-instructions-label">Special Instructions</label>
                  <textarea
                    className="sm-textarea-conditional"
                    name="specialInstructions"
                    value={formData.specialInstructions}
                    onChange={handleInputChange}
                    placeholder="Additional handling instructions..."
                    rows="2"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Supporting Documents Section */}
          <div className="sm-section-wrapper">
            <div className="sm-section-header-with-bar">
              <h3 className="sm-section-title sm-section-title-orange">Supporting Documents</h3>
            </div>
            <div className="sm-section sm-section-documents">
              <div className="sm-documents-content">
                {/* Required Documents */}
                <div className="sm-document-group">
                  <h4 className="sm-document-group-title">Required Documents</h4>
                  <div className="sm-document-fields">
                  <div className="sm-file-upload-card">
                    <div className="sm-file-upload-row">
                      <label className="sm-file-upload-label">Invoice Copy<span className="sm-required">*</span></label>
                      <div>
                        <input
                          type="file"
                          id="invoiceCopy"
                          accept=".pdf,.png,.jpg,.jpeg"
                          onChange={(e) => handleFileUpload('invoiceCopy', e.target.files[0])}
                          className="sm-file-input"
                        />
                        <label htmlFor="invoiceCopy" className="sm-file-upload-button">
                          <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="sm-upload-icon">
                            <path d="M12 3v12"></path>
                            <path d="m17 8-5-5-5 5"></path>
                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                          </svg>
                          Upload
                        </label>
                      </div>
                    </div>
                    {(documents.invoiceCopy || uploadedDocuments.invoiceCopy) && (
                      <div className="sm-file-name">
                        {documents.invoiceCopy ? documents.invoiceCopy.name : 'File uploaded'}
                      </div>
                    )}
                  </div>
                  <div className="sm-file-upload-card">
                    <div className="sm-file-upload-row">
                      <label className="sm-file-upload-label">E-way Bill</label>
                      <div>
                        <input
                          type="file"
                          id="ewayBill"
                          accept=".pdf,.png,.jpg,.jpeg"
                          onChange={(e) => handleFileUpload('ewayBill', e.target.files[0])}
                          className="sm-file-input"
                        />
                        <label htmlFor="ewayBill" className="sm-file-upload-button">
                          <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="sm-upload-icon">
                            <path d="M12 3v12"></path>
                            <path d="m17 8-5-5-5 5"></path>
                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                          </svg>
                          Upload
                        </label>
                      </div>
                    </div>
                    {(documents.ewayBill || uploadedDocuments.ewayBill) && (
                      <div className="sm-file-name">
                        {documents.ewayBill ? documents.ewayBill.name : 'File uploaded'}
                      </div>
                    )}
                  </div>
                  </div>
                </div>
                {/* Optional Documents */}
                <div className="sm-document-group">
                  <h4 className="sm-document-group-title">Optional Documents</h4>
                  <div className="sm-document-fields sm-document-grid">
                    <div className="sm-file-upload-card">
                      <label className="sm-file-upload-label-vertical">Packing List</label>
                      <input
                        type="file"
                        id="packingList"
                        accept=".pdf,.png,.jpg,.jpeg"
                        onChange={(e) => handleFileUpload('packingList', e.target.files[0])}
                        className="sm-file-input"
                      />
                      <label htmlFor="packingList" className="sm-file-upload-button-full">
                        <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="sm-upload-icon">
                          <path d="M12 3v12"></path>
                          <path d="m17 8-5-5-5 5"></path>
                          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                        </svg>
                        Upload
                      </label>
                      {(documents.packingList || uploadedDocuments.packingList) && (
                        <div className="sm-file-name">
                          {documents.packingList ? documents.packingList.name : 'File uploaded'}
                        </div>
                      )}
                    </div>
                    <div className="sm-file-upload-card">
                      <label className="sm-file-upload-label-vertical">Quality Cert.</label>
                      <input
                        type="file"
                        id="qualityCert"
                        accept=".pdf,.png,.jpg,.jpeg"
                        onChange={(e) => handleFileUpload('qualityCert', e.target.files[0])}
                        className="sm-file-input"
                      />
                      <label htmlFor="qualityCert" className="sm-file-upload-button-full">
                        <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="sm-upload-icon">
                          <path d="M12 3v12"></path>
                          <path d="m17 8-5-5-5 5"></path>
                          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                        </svg>
                        Upload
                      </label>
                      {(documents.qualityCert || uploadedDocuments.qualityCert) && (
                        <div className="sm-file-name">
                          {documents.qualityCert ? documents.qualityCert.name : 'File uploaded'}
                        </div>
                      )}
                    </div>
                    <div className="sm-file-upload-card">
                      <label className="sm-file-upload-label-vertical">Cert. of Origin</label>
                      <input
                        type="file"
                        id="certOfOrigin"
                        accept=".pdf,.png,.jpg,.jpeg"
                        onChange={(e) => handleFileUpload('certOfOrigin', e.target.files[0])}
                        className="sm-file-input"
                      />
                      <label htmlFor="certOfOrigin" className="sm-file-upload-button-full">
                        <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="sm-upload-icon">
                          <path d="M12 3v12"></path>
                          <path d="m17 8-5-5-5 5"></path>
                          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                        </svg>
                        Upload
                      </label>
                      {(documents.certOfOrigin || uploadedDocuments.certOfOrigin) && (
                        <div className="sm-file-name">
                          {documents.certOfOrigin ? documents.certOfOrigin.name : 'File uploaded'}
                        </div>
                      )}
                    </div>
                    <div className="sm-file-upload-card">
                      <label className="sm-file-upload-label-vertical">Other Docs</label>
                      <input
                        type="file"
                        id="otherDocs"
                        accept=".pdf,.png,.jpg,.jpeg"
                        onChange={(e) => handleFileUpload('otherDocs', e.target.files[0])}
                        className="sm-file-input"
                      />
                      <label htmlFor="otherDocs" className="sm-file-upload-button-full">
                        <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="sm-upload-icon">
                          <path d="M12 3v12"></path>
                          <path d="m17 8-5-5-5 5"></path>
                          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                        </svg>
                        Upload
                      </label>
                      {(documents.otherDocs || uploadedDocuments.otherDocs) && (
                        <div className="sm-file-name">
                          {documents.otherDocs ? documents.otherDocs.name : 'File uploaded'}
                        </div>
                      )}
                    </div>
                  </div>
                  <p className="sm-file-format-note">PDF, PNG, JPEG (Max 10MB)</p>
                </div>
              </div>
            </div>
          </div>
          </div>


          {/* Additional Notes / Remarks Section */}
          <div className="sm-section-wrapper">
            <div className="sm-section-header-with-bar">
              <h3 className="sm-section-title sm-section-title-gray">Additional Notes / Remarks</h3>
            </div>
            <div className="sm-section sm-section-notes">
              <div className="sm-form-field sm-form-field-full">
                <textarea
                  className="sm-textarea"
                  name="additionalNotes"
                  value={formData.additionalNotes}
                  onChange={handleInputChange}
                  placeholder="Include any special handling instructions, delivery notes, loading/unloading requirements, customs clearance notes, or other relevant information..."
                  rows="3"
                />
              </div>
            </div>
          </div>

        </div>
      </div>

      {/* Preview Modal */}
      {showPreviewModal && (
        <div className="sm-preview-modal-overlay" onClick={() => setShowPreviewModal(false)}>
          <div className="sm-preview-modal" onClick={(e) => e.stopPropagation()}>
            <div className="sm-preview-modal-header">
              <div className="sm-preview-modal-header-left">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="sm-preview-icon">
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                  <circle cx="12" cy="12" r="3"></circle>
                </svg>
                <div>
                  <h2 className="sm-preview-modal-title">Preview Shipping Manifest</h2>
                  <p className="sm-preview-modal-subtitle">Review all details before submitting</p>
                </div>
              </div>
              <button className="sm-preview-modal-close" onClick={() => setShowPreviewModal(false)}>
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>

            <div className="sm-preview-modal-body">
              {/* From Section */}
              <div className="sm-preview-section">
                <h3 className="sm-preview-section-title sm-preview-section-title-orange">From (Exporter/Shipper)</h3>
                <div className="sm-preview-grid">
                  <div className="sm-preview-item">
                    <label>Company / Shipper Name</label>
                    <p>{formData.fromCompanyName}</p>
                  </div>
                  <div className="sm-preview-item">
                    <label>Contact Person</label>
                    <p>{formData.fromContactPerson}</p>
                  </div>
                  <div className="sm-preview-item">
                    <label>GSTIN</label>
                    <p>{formData.fromGSTIN}</p>
                  </div>
                  <div className="sm-preview-item">
                    <label>IEC / Export Code</label>
                    <p>{formData.fromIEC}</p>
                  </div>
                  <div className="sm-preview-item">
                    <label>Address</label>
                    <p>{formData.fromAddressLine1} {formData.fromAddressLine2 && formData.fromAddressLine2}</p>
                  </div>
                  <div className="sm-preview-item">
                    <label>Location</label>
                    <p>{formData.fromCity}, {formData.fromState}, {formData.fromCountry} - {formData.fromPostalCode}</p>
                  </div>
                  <div className="sm-preview-item">
                    <label>Email</label>
                    <p>{formData.fromEmail}</p>
                  </div>
                  <div className="sm-preview-item">
                    <label>Phone</label>
                    <p>{formData.fromPhone}</p>
                  </div>
                </div>
              </div>

              {/* To Section */}
              <div className="sm-preview-section">
                <h3 className="sm-preview-section-title sm-preview-section-title-green">To (Consignee/Importer)</h3>
                <div className="sm-preview-grid">
                  <div className="sm-preview-item">
                    <label>Company / Consignee Name</label>
                    <p>{formData.toCompanyName}</p>
                  </div>
                  <div className="sm-preview-item">
                    <label>Contact Person</label>
                    <p>{formData.toContactPerson}</p>
                  </div>
                  <div className="sm-preview-item">
                    <label>Tax ID / Import Code</label>
                    <p>{formData.toTaxID}</p>
                  </div>
                  <div className="sm-preview-item">
                    <label>Email</label>
                    <p>{formData.toEmail}</p>
                  </div>
                  <div className="sm-preview-item">
                    <label>Address</label>
                    <p>{formData.toAddressLine1} {formData.toAddressLine2 && formData.toAddressLine2}</p>
                  </div>
                  <div className="sm-preview-item">
                    <label>Location</label>
                    <p>{formData.toCity}, {formData.toState}, {formData.toCountry} - {formData.toPostalCode}</p>
                  </div>
                  <div className="sm-preview-item">
                    <label>Phone</label>
                    <p>{formData.toPhone}</p>
                  </div>
                </div>
              </div>

              {/* Order Reference Details (hide for logistics flow) */}
              {!isLogisticsFlow && (
                <div className="sm-preview-section">
                  <h3 className="sm-preview-section-title sm-preview-section-title-blue">Order Reference Details</h3>
                  <div className="sm-preview-grid">
                    <div className="sm-preview-item">
                      <label>PO Number</label>
                      <p>{formData.poNumber}</p>
                    </div>
                    <div className="sm-preview-item">
                      <label>Order Date</label>
                      <p>{formData.orderDate}</p>
                    </div>
                    <div className="sm-preview-item">
                      <label>Currency</label>
                      <p>{formData.currency}</p>
                    </div>
                    <div className="sm-preview-item">
                      <label>Subtotal Amount</label>
                      <p>{formData.currency} {formData.subtotalAmount}</p>
                    </div>
                    <div className="sm-preview-item">
                      <label>Buyer Name</label>
                      <p>{formData.buyerName}</p>
                    </div>
                    <div className="sm-preview-item">
                      <label>Supplier Name</label>
                      <p>{formData.supplierName}</p>
                    </div>
                    <div className="sm-preview-item">
                      <label>Commercial Invoice Number</label>
                      <p>{formData.commercialInvoiceNumber}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Shipment Details */}
              <div className="sm-preview-section">
                <h3 className="sm-preview-section-title sm-preview-section-title-purple">Shipment Details</h3>
                <div className="sm-preview-grid">
                  <div className="sm-preview-item">
                    <label>Preferred Delivery Port</label>
                    <p>{formData.preferredDeliveryPort}</p>
                  </div>
                  <div className="sm-preview-item">
                    <label>Delivery Port Reference</label>
                    <p>{formData.deliveryPortReference || 'N/A'}</p>
                  </div>
                  {!isLogisticsFlow && (
                    <div className="sm-preview-item">
                      <label>Invoice/Bill Number</label>
                      <p>{formData.invoiceBillNumber}</p>
                    </div>
                  )}
                  <div className="sm-preview-item">
                    <label>E-way Bill Number</label>
                    <p>{formData.ewayBillNumber || 'Not generated'}</p>
                  </div>
                  <div className="sm-preview-item">
                    <label>Mode of Transport</label>
                    <p>
                      {modeOfTransportOptions?.find((o) => String(o.value) === String(formData.modeOfTransport))
                        ?.label || formData.modeOfTransport}
                    </p>
                  </div>
                  <div className="sm-preview-item">
                    <label>Incoterms</label>
                    <p>
                      {incotermsOptions?.find((o) => String(o.value) === String(formData.incoterms))?.label ||
                        formData.incoterms}
                    </p>
                  </div>
                  <div className="sm-preview-item">
                    <label>Estimated Delivery Date</label>
                    <p>{formData.estimatedDeliveryDate || 'N/A'}</p>
                  </div>
                </div>
              </div>

              {/* Vehicle & Driver Details */}
              <div className="sm-preview-section">
                <h3 className="sm-preview-section-title sm-preview-section-title-indigo">Vehicle & Driver Details</h3>
                <div className="sm-preview-grid">
                  <div className="sm-preview-item">
                    <label>Vehicle Type</label>
                    <p>{formData.vehicleType}</p>
                  </div>
                  <div className="sm-preview-item">
                    <label>Vehicle Number</label>
                    <p>{formData.vehicleNumber || 'N/A'}</p>
                  </div>
                  <div className="sm-preview-item">
                    <label>Vehicle Capacity</label>
                    <p>{formData.vehicleCapacity || 'N/A'}</p>
                  </div>
                  <div className="sm-preview-item">
                    <label>LR Number</label>
                    <p>{formData.lrNumber}</p>
                  </div>
                  <div className="sm-preview-item">
                    <label>GPS Tracking ID</label>
                    <p>{formData.gpsTrackingID || 'N/A'}</p>
                  </div>
                  <div className="sm-preview-item">
                    <label>GPS Provider</label>
                    <p>{formData.gpsProvider || 'N/A'}</p>
                  </div>
                  <div className="sm-preview-item">
                    <label>Insurance Policy No.</label>
                    <p>{formData.insurancePolicyNo || 'N/A'}</p>
                  </div>
                  <div className="sm-preview-item">
                    <label>Insurance Provider</label>
                    <p>{formData.insuranceProvider || 'N/A'}</p>
                  </div>
                  <div className="sm-preview-item">
                    <label>Driver Name</label>
                    <p>{formData.driverName || 'N/A'}</p>
                  </div>
                  <div className="sm-preview-item">
                    <label>Driver License No.</label>
                    <p>{formData.driverLicenseNo || 'N/A'}</p>
                  </div>
                  <div className="sm-preview-item">
                    <label>Driver Contact Number</label>
                    <p>{formData.driverContactNumber || 'N/A'}</p>
                  </div>
                </div>
              </div>

              {/* Items & Packages */}
              <div className="sm-preview-section">
                <h3 className="sm-preview-section-title sm-preview-section-title-orange">Items & Packages Details</h3>
                <div className="sm-preview-table-wrapper">
                  <table className="sm-preview-table">
                    <thead>
                      <tr>
                        <th>HSN/Product Code</th>
                        <th>Product Description</th>
                        <th>Qty Shipped</th>
                        <th>Qty Total</th>
                        <th>Shipping Qty</th>
                        <th>UOM</th>
                        <th>Package Type</th>
                        <th>No. Packages</th>
                        <th>Weight/Pkg (kg)</th>
                        <th>Total Weight</th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map(item => (
                        <tr key={item.id}>
                          <td>{item.hsnProductCode}</td>
                          <td>{item.productDescription}</td>
                          <td>{item.qtyShipped}</td>
                          <td>{item.qtyTotal}</td>
                          <td>{item.shippingQty}</td>
                          <td>{getUomLabel(item.uom) || '-'}</td>
                          <td>{item.packageType}</td>
                          <td>{item.noPackages}</td>
                          <td>{item.weightPerPkg}</td>
                          <td>{item.totalWeight} kg</td>
                        </tr>
                      ))}
                      <tr className="sm-preview-total-row">
                      <td colSpan="1"></td>
                  <td className="sm-totals-label">Totals:</td>
                  <td colSpan="5"></td>
                  <td className="sm-total-value">{totalPackages}</td>
                  <td colSpan="1"></td>
                  <td className="sm-total-value">{totalWeight} kg</td>
                  

                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Goods Handling Requirements */}
              <div className="sm-preview-section">
                <h3 className="sm-preview-section-title sm-preview-section-title-red">Goods Handling Requirements</h3>
                <div className="sm-preview-grid">
                  <div className="sm-preview-item">
                    <label>Hazardous Material</label>
                    <p>{formData.hazardousMaterial ? 'Yes' : 'No'}</p>
                  </div>
                  {formData.hazardousMaterial && (
                    <>
                      <div className="sm-preview-item">
                        <label>Hazard Class</label>
                        <p>{formData.hazardClass || 'N/A'}</p>
                      </div>
                      <div className="sm-preview-item">
                        <label>UN Number</label>
                        <p>{formData.unNumber || 'N/A'}</p>
                      </div>
                    </>
                  )}
                  <div className="sm-preview-item">
                    <label>Temperature Controlled</label>
                    <p>{formData.temperatureControlled ? 'Yes' : 'No'}</p>
                  </div>
                  {formData.temperatureControlled && (
                    <div className="sm-preview-item">
                      <label>Temperature Range</label>
                      <p>{formData.temperatureRange || 'N/A'}</p>
                    </div>
                  )}
                  <div className="sm-preview-item">
                    <label>Fragile</label>
                    <p>{formData.fragile ? 'Yes' : 'No'}</p>
                  </div>
                  <div className="sm-preview-item">
                    <label>Stackable</label>
                    <p>{formData.stackable ? 'Yes' : 'No'}</p>
                  </div>
                  {(formData.fragile || formData.stackable) && (
                    <>
                      <div className="sm-preview-item">
                        <label>Min Height</label>
                        <p>{formData.fragileMinHeight || 'N/A'}</p>
                      </div>
                      <div className="sm-preview-item">
                        <label>Max Height</label>
                        <p>{formData.stackableMaxHeight || 'N/A'}</p>
                      </div>
                    </>
                  )}
                  <div className="sm-preview-item">
                    <label>Keep Dry</label>
                    <p>{formData.keepDry ? 'Yes' : 'No'}</p>
                  </div>
                  <div className="sm-preview-item">
                    <label>Keep Upright</label>
                    <p>{formData.keepUpright ? 'Yes' : 'No'}</p>
                  </div>
                  {formData.specialInstructions && (
                    <div className="sm-preview-item sm-preview-item-full">
                      <label>Special Instructions</label>
                      <p>{formData.specialInstructions}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Supporting Documents */}
              <div className="sm-preview-section">
                <h3 className="sm-preview-section-title sm-preview-section-title-orange">Supporting Documents</h3>
                <div className="sm-preview-grid">
                  <div className="sm-preview-item">
                    <label>Invoice Copy</label>
                    <p>{(documents.invoiceCopy || uploadedDocuments.invoiceCopy) ? (documents.invoiceCopy ? documents.invoiceCopy.name : 'File uploaded') : 'Not uploaded'}</p>
                  </div>
                  <div className="sm-preview-item">
                    <label>E-way Bill</label>
                    <p>{(documents.ewayBill || uploadedDocuments.ewayBill) ? (documents.ewayBill ? documents.ewayBill.name : 'File uploaded') : 'Not uploaded'}</p>
                  </div>
                  <div className="sm-preview-item">
                    <label>Packing List</label>
                    <p>{(documents.packingList || uploadedDocuments.packingList) ? (documents.packingList ? documents.packingList.name : 'File uploaded') : 'Not uploaded'}</p>
                  </div>
                  <div className="sm-preview-item">
                    <label>Quality Cert.</label>
                    <p>{(documents.qualityCert || uploadedDocuments.qualityCert) ? (documents.qualityCert ? documents.qualityCert.name : 'File uploaded') : 'Not uploaded'}</p>
                  </div>
                  <div className="sm-preview-item">
                    <label>Cert. of Origin</label>
                    <p>{(documents.certOfOrigin || uploadedDocuments.certOfOrigin) ? (documents.certOfOrigin ? documents.certOfOrigin.name : 'File uploaded') : 'Not uploaded'}</p>
                  </div>
                  <div className="sm-preview-item">
                    <label>Other Docs</label>
                    <p>{(documents.otherDocs || uploadedDocuments.otherDocs) ? (documents.otherDocs ? documents.otherDocs.name : 'File uploaded') : 'Not uploaded'}</p>
                  </div>
                </div>
              </div>

              {/* Additional Notes */}
              {formData.additionalNotes && (
                <div className="sm-preview-section">
                  <h3 className="sm-preview-section-title sm-preview-section-title-gray">Additional Notes / Remarks</h3>
                  <p className="sm-preview-text">{formData.additionalNotes}</p>
                </div>
              )}
            </div>

            <div className="sm-preview-modal-footer">
              <button className="sm-btn sm-btn-secondary" onClick={() => setShowPreviewModal(false)}>
                Close
              </button>
              <button 
                className="sm-btn sm-btn-primary" 
                onClick={() => {
                setShowPreviewModal(false);
                  handleSubmit();
                }}
                disabled={loading}
              >
                {loading ? 'Submitting...' : 'Submit Manifest'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View Manifest Details Modal (read-only, auto-opened from "View Manifest") */}
      {showViewManifestModal && (
        <div className="sm-preview-modal-overlay" onClick={() => setShowViewManifestModal(false)}>
          <div className="sm-preview-modal" onClick={(e) => e.stopPropagation()}>
            <div className="sm-preview-modal-header">
              <div className="sm-preview-modal-header-left">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="sm-preview-icon"
                >
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                  <circle cx="12" cy="12" r="3"></circle>
                </svg>
                <div>
                  <h2 className="sm-preview-modal-title">Shipping Manifest Details</h2>
                  <p className="sm-preview-modal-subtitle">
                    Status: {loadedManifestStatus || '—'}
                    {formData.manifestNumber ? ` • Manifest No: ${formData.manifestNumber}` : ''}
                  </p>
                </div>
              </div>
              <button className="sm-preview-modal-close" onClick={() => setShowViewManifestModal(false)}>
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>

            {/* Reuse the same preview body so it shows ALL fields */}
            <div className="sm-preview-modal-body">
              {/* From Section */}
              <div className="sm-preview-section">
                <h3 className="sm-preview-section-title sm-preview-section-title-orange">From (Exporter/Shipper)</h3>
                <div className="sm-preview-grid">
                  <div className="sm-preview-item">
                    <label>Company / Shipper Name</label>
                    <p>{formData.fromCompanyName}</p>
                  </div>
                  <div className="sm-preview-item">
                    <label>Contact Person</label>
                    <p>{formData.fromContactPerson}</p>
                  </div>
                  <div className="sm-preview-item">
                    <label>GSTIN</label>
                    <p>{formData.fromGSTIN}</p>
                  </div>
                  <div className="sm-preview-item">
                    <label>IEC / Export Code</label>
                    <p>{formData.fromIEC}</p>
                  </div>
                  <div className="sm-preview-item">
                    <label>Address</label>
                    <p>{formData.fromAddressLine1} {formData.fromAddressLine2 && formData.fromAddressLine2}</p>
                  </div>
                  <div className="sm-preview-item">
                    <label>Location</label>
                    <p>{formData.fromCity}, {formData.fromState}, {formData.fromCountry} - {formData.fromPostalCode}</p>
                  </div>
                  <div className="sm-preview-item">
                    <label>Email</label>
                    <p>{formData.fromEmail}</p>
                  </div>
                  <div className="sm-preview-item">
                    <label>Phone</label>
                    <p>{formData.fromPhone}</p>
                  </div>
                </div>
              </div>

              {/* To Section */}
              <div className="sm-preview-section">
                <h3 className="sm-preview-section-title sm-preview-section-title-green">To (Consignee/Importer)</h3>
                <div className="sm-preview-grid">
                  <div className="sm-preview-item">
                    <label>Company / Consignee Name</label>
                    <p>{formData.toCompanyName}</p>
                  </div>
                  <div className="sm-preview-item">
                    <label>Contact Person</label>
                    <p>{formData.toContactPerson}</p>
                  </div>
                  <div className="sm-preview-item">
                    <label>Tax ID / Import Code</label>
                    <p>{formData.toTaxID}</p>
                  </div>
                  <div className="sm-preview-item">
                    <label>Email</label>
                    <p>{formData.toEmail}</p>
                  </div>
                  <div className="sm-preview-item">
                    <label>Address</label>
                    <p>{formData.toAddressLine1} {formData.toAddressLine2 && formData.toAddressLine2}</p>
                  </div>
                  <div className="sm-preview-item">
                    <label>Location</label>
                    <p>{formData.toCity}, {formData.toState}, {formData.toCountry} - {formData.toPostalCode}</p>
                  </div>
                  <div className="sm-preview-item">
                    <label>Phone</label>
                    <p>{formData.toPhone}</p>
                  </div>
                </div>
              </div>

              {/* Order Reference Details (hide for logistics flow) */}
              {!isLogisticsFlow && (
                <div className="sm-preview-section">
                  <h3 className="sm-preview-section-title sm-preview-section-title-blue">Order Reference Details</h3>
                  <div className="sm-preview-grid">
                    <div className="sm-preview-item">
                      <label>PO Number</label>
                      <p>{formData.poNumber}</p>
                    </div>
                    <div className="sm-preview-item">
                      <label>Order Date</label>
                      <p>{formData.orderDate}</p>
                    </div>
                    <div className="sm-preview-item">
                      <label>Currency</label>
                      <p>{formData.currency}</p>
                    </div>
                    <div className="sm-preview-item">
                      <label>Subtotal Amount</label>
                      <p>{formData.currency} {formData.subtotalAmount}</p>
                    </div>
                    <div className="sm-preview-item">
                      <label>Buyer Name</label>
                      <p>{formData.buyerName}</p>
                    </div>
                    <div className="sm-preview-item">
                      <label>Supplier Name</label>
                      <p>{formData.supplierName}</p>
                    </div>
                    <div className="sm-preview-item">
                      <label>Commercial Invoice Number</label>
                      <p>{formData.commercialInvoiceNumber}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Shipment Details */}
              <div className="sm-preview-section">
                <h3 className="sm-preview-section-title sm-preview-section-title-purple">Shipment Details</h3>
                <div className="sm-preview-grid">
                  <div className="sm-preview-item">
                    <label>Preferred Delivery Port</label>
                    <p>{formData.preferredDeliveryPort}</p>
                  </div>
                  <div className="sm-preview-item">
                    <label>Delivery Port Reference</label>
                    <p>{formData.deliveryPortReference || 'N/A'}</p>
                  </div>
                  {!isLogisticsFlow && (
                    <div className="sm-preview-item">
                      <label>Invoice/Bill Number</label>
                      <p>{formData.invoiceBillNumber}</p>
                    </div>
                  )}
                  <div className="sm-preview-item">
                    <label>E-way Bill Number</label>
                    <p>{formData.ewayBillNumber || 'Not generated'}</p>
                  </div>
                  <div className="sm-preview-item">
                    <label>Mode of Transport</label>
                    <p>
                      {modeOfTransportOptions?.find((o) => String(o.value) === String(formData.modeOfTransport))?.label ||
                        formData.modeOfTransport}
                    </p>
                  </div>
                  <div className="sm-preview-item">
                    <label>Incoterms</label>
                    <p>
                      {incotermsOptions?.find((o) => String(o.value) === String(formData.incoterms))?.label || formData.incoterms}
                    </p>
                  </div>
                  <div className="sm-preview-item">
                    <label>Estimated Delivery Date</label>
                    <p>{formData.estimatedDeliveryDate || 'N/A'}</p>
                  </div>
                </div>
              </div>

              {/* Items Summary */}
              <div className="sm-preview-section">
                <h3 className="sm-preview-section-title sm-preview-section-title-gray">Items</h3>
                <div className="sm-preview-grid">
                  <div className="sm-preview-item">
                    <label>Total Packages</label>
                    <p>{totalPackages || 0}</p>
                  </div>
                  <div className="sm-preview-item">
                    <label>Total Weight (KG)</label>
                    <p>{totalWeight || 0}</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="sm-preview-modal-footer">
              <button className="sm-btn sm-btn-secondary" onClick={() => setShowViewManifestModal(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

